import { supabase } from '@/utils/supabase/client';

const STORAGE_KEY = 'conectacrm.local.db.v1';
const STORAGE_VERSION = 4;
const ALL_MODULES = ['dashboard', 'leads', 'contacts', 'companies', 'opportunities', 'pipeline', 'activities', 'reports', 'settings'];
const ALL_ACTIONS = ['view', 'create', 'edit', 'delete'];
const LEAD_STATUSES = ['nuevo', 'contactado', 'calificado', 'descartado', 'convertido'];
const LEAD_SOURCE_LABELS = ['web', 'referido', 'evento', 'linkedin', 'whatsapp', 'email'];
const OPPORTUNITY_HEALTH_THRESHOLDS = { healthy: 80, risk: 50 };
const OPPORTUNITY_HEALTH_ACTIVITY_RECENT_DAYS = 2;
const OPPORTUNITY_NO_ACTIVITY_DAYS = 7;
const BANT_KEYS = ['budget', 'authority', 'need', 'urgency'];
const LOST_REASONS = ['Precio', 'Competencia', 'Presupuesto', 'No responde', 'Sin prioridad', 'Otro'];
const CLOSED_STAGES = ['Cerrado ganado', 'Cerrado perdido'];
const STAGE_PROBABILITIES: Record<string, number> = {
  Prospecto: 15,
  'Contacto realizado': 30,
  'Reunion / demo': 45,
  'Propuesta enviada': 60,
  Negociacion: 75,
  'Cerrado ganado': 100,
  'Cerrado perdido': 0,
};

const DEFAULT_ROLES = {
  admin: {
    key: 'admin',
    name: 'Administrador',
    description: 'Acceso total al sistema',
    permissions: {
      dashboard: ['view'],
      leads: [...ALL_ACTIONS],
      contacts: [...ALL_ACTIONS],
      companies: [...ALL_ACTIONS],
      opportunities: [...ALL_ACTIONS],
      pipeline: ['view', 'edit'],
      activities: [...ALL_ACTIONS],
      reports: ['view'],
      settings: ['view', 'manage_users', 'manage_roles', 'manage_org'],
    },
    is_default: true,
  },
  manager: {
    key: 'manager',
    name: 'Manager',
    description: 'Gestion comercial y supervisión',
    permissions: {
      dashboard: ['view'],
      leads: [...ALL_ACTIONS],
      contacts: [...ALL_ACTIONS],
      companies: [...ALL_ACTIONS],
      opportunities: [...ALL_ACTIONS],
      pipeline: ['view', 'edit'],
      activities: [...ALL_ACTIONS],
      reports: ['view'],
      settings: ['view'],
    },
    is_default: true,
  },
  vendedor: {
    key: 'vendedor',
    name: 'Vendedor',
    description: 'Gestion comercial',
    permissions: {
      dashboard: ['view'],
      leads: ['view', 'create', 'edit'],
      contacts: ['view', 'create', 'edit'],
      companies: ['view', 'create', 'edit'],
      opportunities: ['view', 'create', 'edit'],
      pipeline: ['view', 'edit'],
      activities: ['view', 'create', 'edit'],
      reports: ['view'],
      settings: ['view'],
    },
    is_default: true,
  },
  viewer: {
    key: 'viewer',
    name: 'Solo Lectura',
    description: 'Solo visualizacion',
    permissions: Object.fromEntries(ALL_MODULES.map((moduleName) => [moduleName, ['view']])),
    is_default: true,
  },
};

const DEFAULT_STAGES = [
  { name: 'Prospecto', order: 1, color: '#94A3B8' },
  { name: 'Contacto realizado', order: 2, color: '#2563EB' },
  { name: 'Reunion / demo', order: 3, color: '#0EA5E9' },
  { name: 'Propuesta enviada', order: 4, color: '#F59E0B' },
  { name: 'Negociacion', order: 5, color: '#8B5CF6' },
  { name: 'Cerrado ganado', order: 6, color: '#22C55E' },
  { name: 'Cerrado perdido', order: 7, color: '#EF4444' },
];

const REGIONES = [
  { name: 'Metropolitana de Santiago', comunas: ['Santiago', 'Las Condes', 'Providencia', 'Vitacura', 'Renca', 'Estacion Central'] },
  { name: 'Valparaiso', comunas: ['Valparaiso', 'Vina del Mar', 'Quilpue'] },
  { name: 'Biobio', comunas: ['Concepcion', 'Talcahuano', 'Los Angeles'] },
  { name: 'Maule', comunas: ['Talca', 'Curico', 'Linares'] },
];

const GIROS = [
  'Tecnologia',
  'Servicios Profesionales',
  'Retail',
  'Logistica',
  'Manufactura',
  'Salud',
  'Educacion',
  'Telecomunicaciones',
  'Finanzas',
];

const SHARED_DEMO_EMAILS = new Set([
  'jluis.delgado@gmail.com',
  'naviakaren@gmail.com',
  'admin@conectacrm.cl',
]);

function nowIso() {
  return new Date().toISOString();
}

function uid(prefix: string) {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `${prefix}_${crypto.randomUUID().replace(/-/g, '')}`;
  }
  return `${prefix}_${Math.random().toString(36).slice(2, 12)}`;
}

function dateOnly(value?: string) {
  if (!value) return '';
  return value.slice(0, 10);
}

function shiftDays(baseDate: Date, days: number) {
  const value = new Date(baseDate);
  value.setDate(value.getDate() + days);
  return value;
}

function normalizeText(value: string) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function normalizeKey(value: string) {
  return normalizeText(value).replace(/[^a-z0-9]/g, '');
}

function fullNameFromEmail(email: string) {
  return email
    .split('@')[0]
    .split(/[._-]/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function toBooleanAnswer(value: any) {
  if (typeof value === 'boolean') return value;
  const normalized = normalizeText(String(value || ''));
  return ['true', 'si', 'yes', '1', 'alto', 'definido', 'claro'].includes(normalized);
}

function bantLabel(value: any) {
  return toBooleanAnswer(value) ? 'Si' : 'No';
}

function buildQualification(raw: any = {}) {
  const qualification = {
    budget: bantLabel(raw.budget),
    authority: bantLabel(raw.authority),
    need: bantLabel(raw.need),
    urgency: bantLabel(raw.urgency),
  };
  const score = BANT_KEYS.reduce((sum, key) => sum + (qualification[key as keyof typeof qualification] === 'Si' ? 25 : 0), 0);
  const temperature = score >= 75 ? 'Caliente' : score >= 40 ? 'Medio' : 'Frio';
  const qualificationType = score >= 75 ? 'Prospecto' : 'Lead';
  return {
    ...qualification,
    score,
    temperature,
    qualification_type: qualificationType,
  };
}

function hasCompleteQualification(lead: any) {
  return Boolean(
    lead?.qualification
    && BANT_KEYS.every((key) => ['Si', 'No'].includes(String(lead.qualification?.[key] || '')))
  );
}

function isQualifiedProspect(lead: any) {
  return lead?.status === 'calificado'
    && hasCompleteQualification(lead)
    && Number(lead?.qualification?.score || 0) >= 75;
}

function sanitizeLeadStatus(status?: string) {
  const normalized = normalizeText(status || '');
  return LEAD_STATUSES.find((item) => normalizeText(item) === normalized) || 'nuevo';
}

function calculateOpportunityMoney(payload: any) {
  const exento = Boolean(payload?.exento);
  const montoNeto = Math.max(0, Number(payload?.monto_neto || 0));
  const iva = exento ? 0 : Math.round(montoNeto * 0.19);
  const totalBruto = Number(payload?.total_bruto ?? montoNeto + iva);
  const amount = Number(payload?.amount ?? totalBruto);
  return {
    exento,
    monto_neto: montoNeto,
    iva,
    total_bruto: totalBruto,
    amount,
    forecast_amount: Math.round(amount * (Number(payload?.probability || 0) / 100)),
  };
}

function buildStageFromPayload(payload: any) {
  const normalized = normalizeText(payload?.stage || 'Prospecto');
  return DEFAULT_STAGES.find((stage) => normalizeText(stage.name) === normalized)?.name || 'Prospecto';
}

function isClosedStage(stage: string) {
  return CLOSED_STAGES.includes(stage);
}

function shouldRequireNextStep(stage: string) {
  return !isClosedStage(stage);
}

function defaultStageSlaDays(stage: string) {
  const normalized = normalizeText(stage || '');
  if (normalized.includes('prospecto')) return 14;
  if (normalized.includes('contacto')) return 7;
  if (normalized.includes('reunion') || normalized.includes('demo')) return 7;
  if (normalized.includes('propuesta')) return 7;
  if (normalized.includes('negociacion')) return 10;
  return 14;
}

function getStageSlaDays(db: any, orgId: string, stage: string) {
  const key = normalizeText(stage || '');
  const table = Array.isArray(db?.stageSla) ? db.stageSla : [];
  const found = table.find((item: any) => item?.tenant_id === orgId && normalizeText(item?.stage_key || item?.stage || '') === key);
  const days = Number(found?.sla_days);
  if (!Number.isNaN(days) && days > 0) return Math.floor(days);
  return defaultStageSlaDays(stage);
}

function defaultProbabilityRange(stage: string): [number, number] {
  const normalized = normalizeText(stage || '');
  if (normalized.includes('prospecto')) return [5, 30];
  if (normalized.includes('contacto')) return [15, 45];
  if (normalized.includes('reunion') || normalized.includes('demo')) return [25, 55];
  if (normalized.includes('propuesta')) return [40, 75];
  if (normalized.includes('negociacion')) return [55, 90];
  if (normalized.includes('cerrado ganado')) return [100, 100];
  if (normalized.includes('cerrado perdido')) return [0, 0];
  return [0, 100];
}

function computeOpportunityHealth(opportunity: any, db: any, orgId: string) {
  const stage = String(opportunity?.stage || 'Prospecto');
  const probability = Math.max(0, Math.min(100, Number(opportunity?.probability || 0)));
  const daysInStage = daysSince(opportunity?.stage_entered_at || opportunity?.updated_at || opportunity?.created_at);
  const slaDays = getStageSlaDays(db, orgId, stage);
  const lastActivityAt = String(opportunity?.last_activity_at || '');
  const daysSinceActivity = lastActivityAt ? daysSince(lastActivityAt) : 999;
  const hasNextStep = Boolean(String(opportunity?.next_step || '').trim() && String(opportunity?.next_step_date || '').trim());
  const nextStepDate = String(opportunity?.next_step_date || '').trim();
  const nextStepIsFuture = nextStepDate ? nextStepDate >= dateOnly(nowIso()) : false;
  const hasPrimaryContact = Boolean(String(opportunity?.contact_id || '').trim());

  let score = 100;
  const reasons: string[] = [];

  if (!hasNextStep || !nextStepIsFuture) {
    score -= 25;
    reasons.push(!hasNextStep ? 'Sin próxima actividad' : `Próxima actividad vencida (${nextStepDate})`);
  } else {
    score += 10;
    reasons.push(`Próxima actividad: ${opportunity.next_step} (${nextStepDate})`);
  }

  if (daysSinceActivity > OPPORTUNITY_NO_ACTIVITY_DAYS) {
    score -= 20;
    reasons.push(`Sin actividad hace ${daysSinceActivity} días`);
  } else if (daysSinceActivity <= OPPORTUNITY_HEALTH_ACTIVITY_RECENT_DAYS) {
    score += 10;
    reasons.push('Actividad reciente');
  }

  if (daysInStage > slaDays) {
    score -= 15;
    reasons.push(`Exceso de días en etapa: ${daysInStage}d (SLA ${slaDays}d)`);
  }

  if (probability >= 70 && daysSinceActivity > OPPORTUNITY_NO_ACTIVITY_DAYS) {
    score -= 15;
    reasons.push(`Probabilidad ${probability}% sin respaldo de actividad`);
  }

  if (!hasPrimaryContact) {
    score -= 10;
    reasons.push('Sin contacto principal');
  }

  const [minProb, maxProb] = defaultProbabilityRange(stage);
  const inRange = probability >= minProb && probability <= maxProb;
  if (inRange) {
    score += 10;
    reasons.push('Avance coherente (probabilidad vs etapa)');
  } else {
    reasons.push(`Probabilidad fuera de rango esperado (${minProb}-${maxProb}%)`);
  }

  score = Math.max(0, Math.min(100, score));
  const band = score >= OPPORTUNITY_HEALTH_THRESHOLDS.healthy
    ? 'saludable'
    : score >= OPPORTUNITY_HEALTH_THRESHOLDS.risk
      ? 'riesgo'
      : 'alto_riesgo';

  let recommendation = '';
  if (!hasNextStep || !nextStepIsFuture) recommendation = 'Definir próxima acción y fecha hoy';
  else if (daysSinceActivity > OPPORTUNITY_NO_ACTIVITY_DAYS) recommendation = 'Contactar cliente y registrar actividad';
  else if (daysInStage > slaDays) recommendation = 'Revisar etapa o bajar probabilidad';
  else if (probability >= 70 && daysSinceActivity > OPPORTUNITY_NO_ACTIVITY_DAYS) recommendation = 'Bajar probabilidad o agendar acción';
  else if (!hasPrimaryContact) recommendation = 'Asignar contacto principal';
  else recommendation = 'Mantener seguimiento y registrar avances';

  return {
    health_score: score,
    health_band: band,
    health_reasons: reasons,
    health_recommendation: recommendation,
    stage_sla_days: slaDays,
    days_since_activity: daysSinceActivity >= 999 ? null : daysSinceActivity,
    next_step_is_future: nextStepIsFuture,
  };
}

function daysSince(value?: string) {
  if (!value) return 0;
  const base = new Date(value);
  const now = new Date();
  return Math.floor((now.getTime() - base.getTime()) / (1000 * 60 * 60 * 24));
}

function inDateRange(value: string | undefined, from?: string, to?: string) {
  if (!value) return true;
  const day = value.slice(0, 10);
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
}

function applyDateFilter(items: any[], from?: string, to?: string) {
  return items.filter((item) => {
    const source = item.created_at || item.updated_at || item.close_date || item.due_date || item.changed_at;
    return inDateRange(source, from, to);
  });
}

function matchesSearch(item: any, term: string, fields: string[]) {
  if (!term) return true;
  const normalized = normalizeText(term);
  return fields.some((field) => normalizeText(String(item[field] || '')).includes(normalized));
}

function csvValue(value: any) {
  const text = String(value ?? '');
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function rowsToCsv(rows: any[]) {
  if (!rows.length) return '';
  const headers = Array.from(new Set(rows.flatMap((row) => Object.keys(row))));
  return [headers.join(','), ...rows.map((row) => headers.map((header) => csvValue(row[header])).join(','))].join('\n');
}

function parseCsv(text: string) {
  const lines = text.split(/\r?\n/).filter(Boolean);
  if (!lines.length) return [];
  const headers = lines[0].split(',').map((item) => item.trim());
  return lines.slice(1).map((line) => {
    const values = line.match(/(".*?"|[^",]+)(?=\s*,|\s*$)/g) || [];
    return headers.reduce((acc, header, index) => {
      acc[header] = String(values[index] || '').replace(/^"|"$/g, '').replace(/""/g, '"');
      return acc;
    }, {} as any);
  });
}

function toBlob(content: string, type: string) {
  return new Blob([content], { type });
}

function arrayToBase64(buffer: ArrayBuffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

function base64ToBlob(base64: string, type: string) {
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new Blob([bytes], { type });
}

function getCurrentMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: dateOnly(start.toISOString()), to: dateOnly(end.toISOString()) };
}

function getOrgRecords(db: any, collection: string, orgId: string) {
  return db[collection].filter((item: any) => item.tenant_id === orgId);
}

function getRoleDefinition(roleKey: string) {
  return DEFAULT_ROLES[roleKey as keyof typeof DEFAULT_ROLES] || DEFAULT_ROLES.viewer;
}

function getPermissionsForRole(roleKey: string) {
  return getRoleDefinition(roleKey).permissions;
}

function getCurrentOrgId(profile?: any) {
  if (typeof window === 'undefined') return profile?.memberships?.[0]?.org_id || 'org_demo';
  const stored = window.localStorage.getItem('currentOrgId');
  return stored || profile?.memberships?.[0]?.org_id || 'org_demo';
}

function getProfileName(db: any, profileId?: string) {
  const profile = db.profiles.find((item: any) => item.id === profileId);
  return profile?.name || '';
}

function buildOwnerSnapshot(db: any, profile: any, ownerId?: string) {
  const resolvedId = ownerId || profile?.id || '';
  return {
    owner_id: resolvedId,
    owner_name: getProfileName(db, resolvedId) || profile?.name || '',
    assigned_to: resolvedId,
  };
}

function buildSessionUser(db: any, profile: any) {
  const memberships = (profile.memberships || []).map((membership: any) => {
    const org = db.organizations.find((item: any) => item.id === membership.org_id);
    return {
      org_id: membership.org_id,
      org_name: org?.name || membership.org_name || 'Organizacion',
      role: membership.role,
    };
  });
  const currentOrgId = getCurrentOrgId(profile);
  const currentMembership = memberships.find((membership: any) => membership.org_id === currentOrgId) || memberships[0];
  return {
    id: profile.id,
    name: profile.name,
    email: profile.email,
    role: currentMembership?.role || profile.role || 'viewer',
    memberships,
    permissions: getPermissionsForRole(currentMembership?.role || profile.role || 'viewer'),
  };
}

function cloneOrgSupportData(db: any, orgId: string) {
  if (!db.roles.some((role: any) => role.tenant_id === orgId)) {
    db.roles.push(...Object.values(DEFAULT_ROLES).map((role) => ({ ...role, id: `${orgId}_${role.key}`, tenant_id: orgId })));
  }
  if (!db.pipelineStages.some((stage: any) => stage.tenant_id === orgId)) {
    db.pipelineStages.push(...DEFAULT_STAGES.map((stage) => ({ id: uid('stage'), tenant_id: orgId, ...stage })));
  }
}

function dedupeCompanyKey(company: any) {
  if (company?.rut) return `rut:${normalizeKey(company.rut)}`;
  return `name:${normalizeKey(company?.name || company?.razon_social || '')}`;
}

function dedupeContactKey(contact: any) {
  if (contact?.email) return `email:${normalizeKey(contact.email)}`;
  return `name:${normalizeKey(contact?.name || '')}:company:${normalizeKey(contact?.company_id || contact?.company_name || '')}`;
}

function seedLead(base: any) {
  const qualification = buildQualification(base.qualification || {});
  return {
    status: 'nuevo',
    discard_reason: '',
    converted_at: '',
    converted_company_id: '',
    converted_contact_id: '',
    converted_opportunity_id: '',
    qualification: qualification,
    qualification_type: qualification.qualification_type,
    ...base,
  };
}

function seedOpportunity(base: any) {
  const stage = buildStageFromPayload(base);
  const probability = Number(base.probability ?? STAGE_PROBABILITIES[stage] ?? 0);
  return {
    stage_entered_at: base.stage_entered_at || base.updated_at || base.created_at || nowIso(),
    next_step: base.next_step || '',
    next_step_date: base.next_step_date || '',
    lost_reason: base.lost_reason || '',
    qualification_source: base.qualification_source || 'manual-qualified',
    ...calculateOpportunityMoney({ ...base, probability }),
    ...base,
    stage,
    probability,
  };
}

function createSeedDb() {
  const orgId = 'org_demo';
  const now = new Date();
  const isoDays = (days: number) => shiftDays(now, days).toISOString();
  const dayOnlyDays = (days: number) => dateOnly(shiftDays(now, days).toISOString());

  const companyRipley = uid('company');
  const companyMovistar = uid('company');
  const companyAlemana = uid('company');
  const companyBanco = uid('company');
  const companyCencosud = uid('company');
  const companySodimac = uid('company');

  const contactRipley = uid('contact');
  const contactMovistar = uid('contact');
  const contactAlemana = uid('contact');
  const contactBanco = uid('contact');
  const contactCencosud = uid('contact');
  const contactSodimac = uid('contact');

  const leadRodrigo = uid('lead');
  const leadPamela = uid('lead');
  const leadIgnacio = uid('lead');
  const leadCarolina = uid('lead');
  const leadJaviera = uid('lead');
  const leadMarcelo = uid('lead');
  const leadDaniela = uid('lead');

  const oppRipley = uid('opp');
  const oppMovistar = uid('opp');
  const oppAlemana = uid('opp');
  const oppBanco = uid('opp');
  const oppCencosud = uid('opp');
  const oppSodimac = uid('opp');

  const historyStage = (opportunityId: string, fromStage: string, toStage: string, changedAt: string, ownerId: string, ownerName: string) => ({
    id: uid('hist'),
    tenant_id: orgId,
    opportunity_id: opportunityId,
    event_type: 'stage_changed',
    changed_at: changedAt,
    actor_id: ownerId,
    actor_name: ownerName,
    from_value: fromStage,
    to_value: toStage,
    summary: `${ownerName} movio la oportunidad a ${toStage}`,
  });

  const historyProbability = (opportunityId: string, fromValue: number, toValue: number, changedAt: string, ownerId: string, ownerName: string) => ({
    id: uid('hist'),
    tenant_id: orgId,
    opportunity_id: opportunityId,
    event_type: 'probability_changed',
    changed_at: changedAt,
    actor_id: ownerId,
    actor_name: ownerName,
    from_value: String(fromValue),
    to_value: String(toValue),
    summary: `${ownerName} ajusto la probabilidad a ${toValue}%`,
  });

  const historyActivity = (opportunityId: string, changedAt: string, ownerId: string, ownerName: string, summary: string) => ({
    id: uid('hist'),
    tenant_id: orgId,
    opportunity_id: opportunityId,
    event_type: 'activity_logged',
    changed_at: changedAt,
    actor_id: ownerId,
    actor_name: ownerName,
    from_value: '',
    to_value: '',
    summary,
  });

  const profiles = [
    {
      id: 'demo_admin',
      name: 'Administrador',
      email: 'admin@conectacrm.cl',
      role: 'admin',
      memberships: [{ org_id: orgId, role: 'admin' }],
      created_at: isoDays(-120),
    },
    {
      id: 'demo_jluis',
      name: 'Jose Luis Delgado',
      email: 'jluis.delgado@gmail.com',
      role: 'manager',
      memberships: [{ org_id: orgId, role: 'manager' }],
      created_at: isoDays(-90),
    },
    {
      id: 'demo_navia',
      name: 'Navia Karen',
      email: 'naviakaren@gmail.com',
      role: 'vendedor',
      memberships: [{ org_id: orgId, role: 'vendedor' }],
      created_at: isoDays(-90),
    },
  ];

  const companies = [
    {
      id: companyRipley,
      tenant_id: orgId,
      name: 'Ripley Chile',
      razon_social: 'Ripley Chile S.A.',
      rut: '96.737.510-K',
      dedupe_key: 'rut:96737510k',
      normalized_name: normalizeKey('Ripley Chile'),
      giro: 'Comercio al por menor',
      industry: 'Retail',
      website: 'www.ripley.cl',
      phone: '+56 2 2597 1000',
      email: 'empresas@ripley.cl',
      address: 'Av. Costanera Sur 2730',
      region: 'Metropolitana de Santiago',
      comuna: 'Las Condes',
      created_at: isoDays(-60),
    },
    {
      id: companyMovistar,
      tenant_id: orgId,
      name: 'Movistar Chile',
      razon_social: 'Telefonica Moviles Chile S.A.',
      rut: '76.124.890-1',
      dedupe_key: 'rut:761248901',
      normalized_name: normalizeKey('Movistar Chile'),
      giro: 'Telecomunicaciones',
      industry: 'Telecomunicaciones',
      website: 'www.movistar.cl',
      phone: '+56 2 2691 2000',
      email: 'negocios@movistar.cl',
      address: 'Av. Providencia 111',
      region: 'Metropolitana de Santiago',
      comuna: 'Providencia',
      created_at: isoDays(-48),
    },
    {
      id: companyAlemana,
      tenant_id: orgId,
      name: 'Clinica Alemana',
      razon_social: 'Clinica Alemana de Santiago S.A.',
      rut: '99.265.000-4',
      dedupe_key: 'rut:992650004',
      normalized_name: normalizeKey('Clinica Alemana'),
      giro: 'Servicios de salud',
      industry: 'Salud',
      website: 'www.alemana.cl',
      phone: '+56 2 2210 1111',
      email: 'empresas@alemana.cl',
      address: 'Av. Vitacura 5951',
      region: 'Metropolitana de Santiago',
      comuna: 'Vitacura',
      created_at: isoDays(-35),
    },
    {
      id: companyBanco,
      tenant_id: orgId,
      name: 'Banco de Chile',
      razon_social: 'Banco de Chile',
      rut: '97.006.000-6',
      dedupe_key: 'rut:970060006',
      normalized_name: normalizeKey('Banco de Chile'),
      giro: 'Servicios financieros',
      industry: 'Finanzas',
      website: 'www.bancochile.cl',
      phone: '+56 2 2650 0000',
      email: 'empresas@bancochile.cl',
      address: "Av. Libertador Bernardo O'Higgins 999",
      region: 'Metropolitana de Santiago',
      comuna: 'Santiago',
      created_at: isoDays(-22),
    },
    {
      id: companyCencosud,
      tenant_id: orgId,
      name: 'Cencosud',
      razon_social: 'Cencosud S.A.',
      rut: '93.834.000-7',
      dedupe_key: 'rut:938340007',
      normalized_name: normalizeKey('Cencosud'),
      giro: 'Retail y consumo masivo',
      industry: 'Retail',
      website: 'www.cencosud.com',
      phone: '+56 2 2950 9000',
      email: 'negocios@cencosud.cl',
      address: 'Avenida Kennedy 9001',
      region: 'Metropolitana de Santiago',
      comuna: 'Las Condes',
      created_at: isoDays(-18),
    },
    {
      id: companySodimac,
      tenant_id: orgId,
      name: 'Sodimac',
      razon_social: 'Sodimac S.A.',
      rut: '96.792.430-K',
      dedupe_key: 'rut:96792430k',
      normalized_name: normalizeKey('Sodimac'),
      giro: 'Retail hogar y construccion',
      industry: 'Retail',
      website: 'www.sodimac.cl',
      phone: '+56 2 2738 7000',
      email: 'empresas@sodimac.cl',
      address: 'Av. Presidente Eduardo Frei Montalva 3092',
      region: 'Metropolitana de Santiago',
      comuna: 'Renca',
      created_at: isoDays(-20),
    },
  ];

  const contacts = [
    {
      id: contactRipley,
      tenant_id: orgId,
      name: 'Claudia Reyes',
      email: 'c.reyes@ripley.cl',
      normalized_email: normalizeKey('c.reyes@ripley.cl'),
      normalized_name: normalizeKey('Claudia Reyes'),
      phone: '+56 9 8111 2222',
      whatsapp: '+56 9 8111 2222',
      preferred_channel: 'whatsapp',
      position: 'Gerente Comercial',
      company_id: companyRipley,
      company_name: 'Ripley Chile',
      dedupe_key: 'email:creyesripleycl',
      created_at: isoDays(-50),
    },
    {
      id: contactMovistar,
      tenant_id: orgId,
      name: 'Lorena Pinto',
      email: 'l.pinto@movistar.cl',
      normalized_email: normalizeKey('l.pinto@movistar.cl'),
      normalized_name: normalizeKey('Lorena Pinto'),
      phone: '+56 9 6333 4444',
      whatsapp: '+56 9 6333 4444',
      preferred_channel: 'email',
      position: 'Directora de Compras',
      company_id: companyMovistar,
      company_name: 'Movistar Chile',
      dedupe_key: 'email:lpintomovistarcl',
      created_at: isoDays(-40),
    },
    {
      id: contactAlemana,
      tenant_id: orgId,
      name: 'Diego Carrasco',
      email: 'd.carrasco@alemana.cl',
      normalized_email: normalizeKey('d.carrasco@alemana.cl'),
      normalized_name: normalizeKey('Diego Carrasco'),
      phone: '+56 9 3666 7777',
      whatsapp: '+56 9 3666 7777',
      preferred_channel: 'llamada',
      position: 'Director TI',
      company_id: companyAlemana,
      company_name: 'Clinica Alemana',
      dedupe_key: 'email:dcarrascoalemanacl',
      created_at: isoDays(-25),
    },
    {
      id: contactBanco,
      tenant_id: orgId,
      name: 'Pamela Gutierrez',
      email: 'pamela.g@bancochile.cl',
      normalized_email: normalizeKey('pamela.g@bancochile.cl'),
      normalized_name: normalizeKey('Pamela Gutierrez'),
      phone: '+56 9 6111 3344',
      whatsapp: '+56 9 6111 3344',
      preferred_channel: 'whatsapp',
      position: 'Subgerente de Canales',
      company_id: companyBanco,
      company_name: 'Banco de Chile',
      dedupe_key: 'email:pamelagbancochilecl',
      created_at: isoDays(-18),
    },
    {
      id: contactCencosud,
      tenant_id: orgId,
      name: 'Javiera Soto',
      email: 'javiera.soto@cencosud.cl',
      normalized_email: normalizeKey('javiera.soto@cencosud.cl'),
      normalized_name: normalizeKey('Javiera Soto'),
      phone: '+56 9 7444 9922',
      whatsapp: '+56 9 7444 9922',
      preferred_channel: 'email',
      position: 'Jefa de Proyectos TI',
      company_id: companyCencosud,
      company_name: 'Cencosud',
      dedupe_key: 'email:javierasotocencosudcl',
      created_at: isoDays(-14),
    },
    {
      id: contactSodimac,
      tenant_id: orgId,
      name: 'Carolina Bravo',
      email: 'carolina.b@sodimac.cl',
      normalized_email: normalizeKey('carolina.b@sodimac.cl'),
      normalized_name: normalizeKey('Carolina Bravo'),
      phone: '+56 9 5567 8904',
      whatsapp: '+56 9 5567 8904',
      preferred_channel: 'email',
      position: 'Subgerenta Ecommerce',
      company_id: companySodimac,
      company_name: 'Sodimac',
      dedupe_key: 'email:carolinabsodimaccl',
      created_at: isoDays(-12),
    },
  ];

  const leads = [
    seedLead({
      id: leadRodrigo,
      tenant_id: orgId,
      owner_id: 'demo_jluis',
      owner_name: 'Jose Luis Delgado',
      name: 'Rodrigo Saavedra',
      email: 'rodrigo.saavedra@ripley.cl',
      phone: '+56 9 8234 5671',
      company: 'Ripley Chile',
      source: 'web',
      status: 'nuevo',
      notes: 'Solicito contacto desde formulario de servicios cloud.',
      created_at: isoDays(-2),
      updated_at: isoDays(-2),
    }),
    seedLead({
      id: leadPamela,
      tenant_id: orgId,
      owner_id: 'demo_jluis',
      owner_name: 'Jose Luis Delgado',
      name: 'Pamela Gutierrez',
      email: 'pamela.g@bancochile.cl',
      phone: '+56 9 7345 6782',
      company: 'Banco de Chile',
      source: 'linkedin',
      status: 'contactado',
      notes: 'Primera reunión agendada para validar necesidad.',
      created_at: isoDays(-8),
      updated_at: isoDays(-3),
    }),
    seedLead({
      id: leadIgnacio,
      tenant_id: orgId,
      owner_id: 'demo_navia',
      owner_name: 'Navia Karen',
      name: 'Ignacio Fuentes',
      email: 'i.fuentes@movistar.cl',
      phone: '+56 9 6456 7893',
      company: 'Movistar Chile',
      source: 'referido',
      status: 'calificado',
      qualification: { budget: true, authority: true, need: true, urgency: false },
      notes: 'Cuenta priorizada para demo ejecutiva.',
      created_at: isoDays(-14),
      updated_at: isoDays(-4),
    }),
    seedLead({
      id: leadCarolina,
      tenant_id: orgId,
      owner_id: 'demo_navia',
      owner_name: 'Navia Karen',
      name: 'Carolina Bravo',
      email: 'carolina.b@sodimac.cl',
      phone: '+56 9 5567 8904',
      company: 'Sodimac',
      source: 'evento',
      status: 'descartado',
      discard_reason: 'No responde',
      notes: 'Lead descartado luego de tres intentos de contacto.',
      created_at: isoDays(-25),
      updated_at: isoDays(-12),
    }),
    seedLead({
      id: leadJaviera,
      tenant_id: orgId,
      owner_id: 'demo_jluis',
      owner_name: 'Jose Luis Delgado',
      name: 'Javiera Soto',
      email: 'javiera.soto@cencosud.cl',
      phone: '+56 9 4123 7788',
      company: 'Cencosud',
      source: 'linkedin',
      status: 'convertido',
      qualification: { budget: true, authority: true, need: true, urgency: true },
      converted_at: isoDays(-2),
      converted_company_id: companyCencosud,
      converted_contact_id: contactCencosud,
      converted_opportunity_id: oppCencosud,
      notes: 'Lead ya convertido a oportunidad activa.',
      created_at: isoDays(-6),
      updated_at: isoDays(-2),
    }),
    seedLead({
      id: leadMarcelo,
      tenant_id: orgId,
      owner_id: 'demo_navia',
      owner_name: 'Navia Karen',
      name: 'Marcelo Pino',
      email: 'marcelo.pino@bancochile.cl',
      phone: '+56 9 5222 1111',
      company: 'Banco de Chile',
      source: 'referido',
      status: 'calificado',
      qualification: { budget: true, authority: false, need: true, urgency: true },
      notes: 'Lead calificado, falta definir presupuesto definitivo.',
      created_at: isoDays(-9),
      updated_at: isoDays(-2),
    }),
    seedLead({
      id: leadDaniela,
      tenant_id: orgId,
      owner_id: 'demo_jluis',
      owner_name: 'Jose Luis Delgado',
      name: 'Daniela Vega',
      email: 'daniela.vega@vitaclinic.cl',
      phone: '+56 9 6777 8800',
      company: 'Vita Clinic',
      source: 'web',
      status: 'nuevo',
      notes: 'Sin gestionar todavia, sirve para alerta de 24h.',
      created_at: isoDays(-3),
      updated_at: isoDays(-3),
    }),
  ];

  const opportunities = [
    seedOpportunity({
      id: oppRipley,
      tenant_id: orgId,
      name: 'ERP Cloud Ripley',
      account_id: companyRipley,
      account_name: 'Ripley Chile',
      contact_id: contactRipley,
      contact_name: 'Claudia Reyes',
      owner_id: 'demo_jluis',
      owner_name: 'Jose Luis Delgado',
      source_lead_id: leadRodrigo,
      qualification_source: 'manual-qualified',
      monto_neto: 85000000,
      stage: 'Negociacion',
      probability: 75,
      close_date: dayOnlyDays(10),
      next_step: 'Enviar versión final de propuesta y confirmar comité',
      next_step_date: dayOnlyDays(2),
      created_at: isoDays(-45),
      updated_at: isoDays(-1),
      stage_entered_at: isoDays(-5),
      last_activity_at: isoDays(-1),
    }),
    seedOpportunity({
      id: oppMovistar,
      tenant_id: orgId,
      name: 'Migracion 5G Movistar',
      account_id: companyMovistar,
      account_name: 'Movistar Chile',
      contact_id: contactMovistar,
      contact_name: 'Lorena Pinto',
      owner_id: 'demo_navia',
      owner_name: 'Navia Karen',
      source_lead_id: leadIgnacio,
      qualification_source: 'lead-conversion',
      monto_neto: 250000000,
      stage: 'Reunion / demo',
      probability: 45,
      close_date: dayOnlyDays(35),
      next_step: 'Agendar demo técnica con arquitectura',
      next_step_date: dayOnlyDays(4),
      created_at: isoDays(-20),
      updated_at: isoDays(-5),
      stage_entered_at: isoDays(-12),
      last_activity_at: isoDays(-2),
    }),
    seedOpportunity({
      id: oppAlemana,
      tenant_id: orgId,
      name: 'Telemedicina Alemana',
      account_id: companyAlemana,
      account_name: 'Clinica Alemana',
      contact_id: contactAlemana,
      contact_name: 'Diego Carrasco',
      owner_id: 'demo_jluis',
      owner_name: 'Jose Luis Delgado',
      source_lead_id: '',
      qualification_source: 'manual-qualified',
      monto_neto: 95000000,
      stage: 'Cerrado ganado',
      probability: 100,
      close_date: dayOnlyDays(-3),
      next_step: '',
      next_step_date: '',
      created_at: isoDays(-35),
      updated_at: isoDays(-3),
      stage_entered_at: isoDays(-3),
      last_activity_at: isoDays(-4),
    }),
    seedOpportunity({
      id: oppBanco,
      tenant_id: orgId,
      name: 'Core Bancario Banco de Chile',
      account_id: companyBanco,
      account_name: 'Banco de Chile',
      contact_id: contactBanco,
      contact_name: 'Pamela Gutierrez',
      owner_id: 'demo_navia',
      owner_name: 'Navia Karen',
      source_lead_id: leadPamela,
      qualification_source: 'manual-qualified',
      monto_neto: 145000000,
      stage: 'Propuesta enviada',
      probability: 60,
      close_date: dayOnlyDays(18),
      next_step: 'Follow-up comercial post propuesta',
      next_step_date: dayOnlyDays(1),
      created_at: isoDays(-16),
      updated_at: isoDays(-2),
      stage_entered_at: isoDays(-8),
      last_activity_at: isoDays(-8),
    }),
    seedOpportunity({
      id: oppCencosud,
      tenant_id: orgId,
      name: 'Programa Omnicanal Cencosud',
      account_id: companyCencosud,
      account_name: 'Cencosud',
      contact_id: contactCencosud,
      contact_name: 'Javiera Soto',
      owner_id: 'demo_jluis',
      owner_name: 'Jose Luis Delgado',
      source_lead_id: leadJaviera,
      qualification_source: 'lead-conversion',
      monto_neto: 198000000,
      stage: 'Prospecto',
      probability: 15,
      close_date: dayOnlyDays(55),
      next_step: 'Validar alcance inicial con equipo de retail',
      next_step_date: dayOnlyDays(-1),
      created_at: isoDays(-12),
      updated_at: isoDays(-1),
      stage_entered_at: isoDays(-16),
      last_activity_at: isoDays(-12),
    }),
    seedOpportunity({
      id: oppSodimac,
      tenant_id: orgId,
      name: 'Analitica Comercial Sodimac',
      account_id: companySodimac,
      account_name: 'Sodimac',
      contact_id: contactSodimac,
      contact_name: 'Carolina Bravo',
      owner_id: 'demo_navia',
      owner_name: 'Navia Karen',
      source_lead_id: leadCarolina,
      qualification_source: 'manual-qualified',
      monto_neto: 52000000,
      stage: 'Cerrado perdido',
      probability: 0,
      close_date: dayOnlyDays(-6),
      lost_reason: 'Competencia',
      next_step: '',
      next_step_date: '',
      created_at: isoDays(-28),
      updated_at: isoDays(-6),
      stage_entered_at: isoDays(-6),
      last_activity_at: isoDays(-10),
    }),
  ];

  const activities = [
    {
      id: uid('activity'),
      tenant_id: orgId,
      type: 'llamada',
      subject: 'Seguimiento propuesta ERP Ripley',
      description: 'Confirmar reunión de firma y validar términos.',
      status: 'pendiente',
      due_date: dayOnlyDays(2),
      created_at: isoDays(-1),
      owner_id: 'demo_jluis',
      owner_name: 'Jose Luis Delgado',
      company_id: companyRipley,
      contact_id: contactRipley,
      opportunity_id: oppRipley,
      related_entity_type: 'opportunity',
      related_entity_id: oppRipley,
      related_to: 'Ripley Chile',
    },
    {
      id: uid('activity'),
      tenant_id: orgId,
      type: 'reunion',
      subject: 'Demo migración Movistar',
      description: 'Revisión técnica del proyecto 5G.',
      status: 'pendiente',
      due_date: dayOnlyDays(4),
      created_at: isoDays(-2),
      owner_id: 'demo_navia',
      owner_name: 'Navia Karen',
      company_id: companyMovistar,
      contact_id: contactMovistar,
      opportunity_id: oppMovistar,
      related_entity_type: 'opportunity',
      related_entity_id: oppMovistar,
      related_to: 'Movistar Chile',
    },
    {
      id: uid('activity'),
      tenant_id: orgId,
      type: 'tarea',
      subject: 'Kickoff Clinica Alemana',
      description: 'Preparar inicio del proyecto ganado.',
      status: 'completada',
      due_date: dayOnlyDays(-2),
      created_at: isoDays(-7),
      owner_id: 'demo_jluis',
      owner_name: 'Jose Luis Delgado',
      company_id: companyAlemana,
      contact_id: contactAlemana,
      opportunity_id: oppAlemana,
      related_entity_type: 'opportunity',
      related_entity_id: oppAlemana,
      related_to: 'Clinica Alemana',
    },
    {
      id: uid('activity'),
      tenant_id: orgId,
      type: 'reunion',
      subject: 'Workshop Core Bancario Banco de Chile',
      description: 'Validar integraciones y alcance funcional.',
      status: 'pendiente',
      due_date: dayOnlyDays(1),
      created_at: isoDays(-8),
      owner_id: 'demo_navia',
      owner_name: 'Navia Karen',
      company_id: companyBanco,
      contact_id: contactBanco,
      opportunity_id: oppBanco,
      related_entity_type: 'opportunity',
      related_entity_id: oppBanco,
      related_to: 'Banco de Chile',
    },
    {
      id: uid('activity'),
      tenant_id: orgId,
      type: 'llamada',
      subject: 'Seguimiento Cencosud omnicanal',
      description: 'Definir siguiente iteración de la propuesta.',
      status: 'pendiente',
      due_date: dayOnlyDays(-1),
      created_at: isoDays(-12),
      owner_id: 'demo_jluis',
      owner_name: 'Jose Luis Delgado',
      company_id: companyCencosud,
      contact_id: contactCencosud,
      opportunity_id: oppCencosud,
      related_entity_type: 'opportunity',
      related_entity_id: oppCencosud,
      related_to: 'Cencosud',
    },
  ];

  const opportunityHistory = [
    historyStage(oppRipley, 'Propuesta enviada', 'Negociacion', isoDays(-5), 'demo_jluis', 'Jose Luis Delgado'),
    historyProbability(oppRipley, 60, 75, isoDays(-5), 'demo_jluis', 'Jose Luis Delgado'),
    historyActivity(oppRipley, isoDays(-1), 'demo_jluis', 'Jose Luis Delgado', 'Actividad registrada: Seguimiento propuesta ERP Ripley'),
    historyStage(oppMovistar, 'Contacto realizado', 'Reunion / demo', isoDays(-12), 'demo_navia', 'Navia Karen'),
    historyProbability(oppMovistar, 30, 45, isoDays(-12), 'demo_navia', 'Navia Karen'),
    historyActivity(oppMovistar, isoDays(-2), 'demo_navia', 'Navia Karen', 'Actividad registrada: Demo migración Movistar'),
    historyStage(oppBanco, 'Reunion / demo', 'Propuesta enviada', isoDays(-8), 'demo_navia', 'Navia Karen'),
    historyProbability(oppBanco, 45, 60, isoDays(-8), 'demo_navia', 'Navia Karen'),
    historyActivity(oppBanco, isoDays(-8), 'demo_navia', 'Navia Karen', 'Actividad registrada: Workshop Core Bancario Banco de Chile'),
    historyStage(oppCencosud, 'Contacto realizado', 'Prospecto', isoDays(-16), 'demo_jluis', 'Jose Luis Delgado'),
    historyProbability(oppCencosud, 30, 15, isoDays(-16), 'demo_jluis', 'Jose Luis Delgado'),
    historyStage(oppAlemana, 'Negociacion', 'Cerrado ganado', isoDays(-3), 'demo_jluis', 'Jose Luis Delgado'),
    historyProbability(oppAlemana, 85, 100, isoDays(-3), 'demo_jluis', 'Jose Luis Delgado'),
    historyStage(oppSodimac, 'Propuesta enviada', 'Cerrado perdido', isoDays(-6), 'demo_navia', 'Navia Karen'),
    historyProbability(oppSodimac, 60, 0, isoDays(-6), 'demo_navia', 'Navia Karen'),
  ];

  return {
    version: STORAGE_VERSION,
    organizations: [
      {
        id: orgId,
        name: 'ConectaCRM Demo',
        rut: '76.543.210-K',
        industry: 'Tecnologia',
        plan: 'trial',
        created_at: isoDays(-120),
      },
    ],
    profiles,
    roles: Object.values(DEFAULT_ROLES).map((role) => ({ ...role, id: `${orgId}_${role.key}`, tenant_id: orgId })),
    pipelineStages: DEFAULT_STAGES.map((stage) => ({ id: uid('stage'), tenant_id: orgId, ...stage })),
    stageSla: DEFAULT_STAGES.map((stage) => ({ id: uid('sla'), tenant_id: orgId, stage_key: stage.name, sla_days: defaultStageSlaDays(stage.name) })),
    companies,
    leads,
    contacts,
    opportunities,
    opportunityHistory,
    activities,
    campaigns: [
      { id: uid('campaign'), tenant_id: orgId, name: 'Lanzamiento Servicios Cloud', status: 'completada', emails_sent: 3500, delivered: 3350, opened: 1675, clicked: 670, bounced: 150, conversions: 134, open_rate: 50, click_rate: 20, bounce_rate: 4.3, conversion_rate: 4, created_at: isoDays(-30) },
      { id: uid('campaign'), tenant_id: orgId, name: 'Webinar Ciberseguridad', status: 'activa', emails_sent: 2200, delivered: 2120, opened: 1060, clicked: 424, bounced: 80, conversions: 85, open_rate: 50, click_rate: 20, bounce_rate: 3.6, conversion_rate: 4, created_at: isoDays(-10) },
      { id: uid('campaign'), tenant_id: orgId, name: 'Newsletter Clientes Q1', status: 'completada', emails_sent: 4800, delivered: 4650, opened: 2325, clicked: 930, bounced: 150, conversions: 186, open_rate: 50, click_rate: 20, bounce_rate: 3.1, conversion_rate: 4, created_at: isoDays(-20) },
    ],
    invitations: [],
    attachments: [],
  };
}

function readDb() {
  if (typeof window === 'undefined') {
    return createSeedDb();
  }
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    const db = createSeedDb();
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(db));
    return db;
  }
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.version !== STORAGE_VERSION) {
      const db = createSeedDb();
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(db));
      return db;
    }
    return parsed;
  } catch {
    const db = createSeedDb();
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(db));
    return db;
  }
}

function writeDb(db: any) {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(db));
  }
  return db;
}

function requireUserProfile(db: any, userId: string) {
  const profile = db.profiles.find((item: any) => item.id === userId);
  if (!profile) {
    throw new Error('Perfil no encontrado');
  }
  return profile;
}

function hydrateOpportunity(opportunity: any, db: any) {
  const company = db.companies.find((item: any) => item.id === opportunity.account_id);
  const contact = db.contacts.find((item: any) => item.id === opportunity.contact_id);
  const lastActivity = db.activities
    .filter((item: any) => item.opportunity_id === opportunity.id)
    .sort((a: any, b: any) => new Date(b.created_at || b.due_date || 0).getTime() - new Date(a.created_at || a.due_date || 0).getTime())[0];

  const hydrated = {
    ...opportunity,
    account_name: company?.name || opportunity.account_name || opportunity.account_id || '',
    contact_name: contact?.name || opportunity.contact_name || '',
    owner_name: getProfileName(db, opportunity.owner_id) || opportunity.owner_name || '',
    forecast_amount: Math.round(Number(opportunity.amount || 0) * (Number(opportunity.probability || 0) / 100)),
    days_in_stage: daysSince(opportunity.stage_entered_at || opportunity.updated_at || opportunity.created_at),
    last_activity_at: lastActivity?.created_at || opportunity.last_activity_at || '',
    last_activity_subject: lastActivity?.subject || '',
  };
  return {
    ...hydrated,
    ...computeOpportunityHealth(hydrated, db, hydrated.tenant_id),
  };
}

function validateLeadPayload(payload: any, existing?: any) {
  const record = { ...(existing || {}), ...(payload || {}) };
  if (!String(record.name || '').trim()) {
    errorResponse('Nombre del lead es obligatorio', 400);
  }
  record.status = sanitizeLeadStatus(record.status);
  if (record.status === 'descartado' && !String(record.discard_reason || '').trim()) {
    errorResponse('Motivo de descarte es obligatorio', 400);
  }
  if (record.status === 'convertido' && !record.converted_opportunity_id) {
    errorResponse('No se puede marcar convertido sin oportunidad asociada', 400);
  }
  record.source = LEAD_SOURCE_LABELS.includes(record.source) ? record.source : 'web';
  if (record.qualification) {
    const qualification = buildQualification(record.qualification);
    record.qualification = qualification;
    record.qualification_type = qualification.qualification_type;
  }
  return record;
}

function validateOpportunityPayload(db: any, payload: any, profile: any, existing?: any) {
  const record = { ...(existing || {}), ...(payload || {}) };
  const stage = buildStageFromPayload(record);
  const probability = Number(record.probability ?? STAGE_PROBABILITIES[stage] ?? 0);
  const sourceLeadId = String(record.source_lead_id || '').trim();
  const qualificationSource = String(record.qualification_source || '').trim();
  const manualMode = Boolean(record.manual_mode);
  const manualModeReason = String(record.manual_mode_reason || '').trim();
  if (!String(record.name || '').trim()) {
    errorResponse('Nombre de oportunidad es obligatorio', 400);
  }
  if (!stage) {
    errorResponse('Etapa es obligatoria', 400);
  }
  if (Number.isNaN(probability) || probability < 0 || probability > 100) {
    errorResponse('Probabilidad invalida', 400);
  }

  const ownerId = String(record.owner_id || record.assigned_to || profile?.id || '').trim();
  if (!ownerId) {
    errorResponse('Responsable es obligatorio', 400);
  }
  if (!db.profiles.find((item: any) => item.id === ownerId)) {
    errorResponse('Responsable no encontrado', 400);
  }

  if (shouldRequireNextStep(stage)) {
    if (!String(record.next_step || '').trim()) {
      errorResponse('Proximo paso es obligatorio', 400);
    }
    if (!String(record.next_step_date || '').trim()) {
      errorResponse('Fecha de proximo paso es obligatoria', 400);
    }
  }

  if (stage === 'Cerrado perdido' && !String(record.lost_reason || '').trim()) {
    errorResponse('Motivo de negocio perdido es obligatorio', 400);
  }

  if (!existing) {
    if (!sourceLeadId) {
      if (qualificationSource !== 'manual-qualified' || !manualMode) {
        errorResponse('La oportunidad debe nacer desde un lead convertido. El modo manual solo se permite para QA/pruebas.', 400);
      }
      if (!manualModeReason) {
        errorResponse('Debe indicar el motivo de la creación manual QA.', 400);
      }
    } else {
      const lead = db.leads.find((item: any) => item.id === sourceLeadId);
      if (!lead || (!isQualifiedProspect(lead) && lead.status !== 'convertido')) {
        errorResponse('El lead origen no esta calificado para crear oportunidad', 400);
      }
    }
  }

  return {
    ...record,
    stage,
    probability,
    ...buildOwnerSnapshot(db, profile, ownerId),
    ...calculateOpportunityMoney({ ...record, probability }),
    manual_mode: manualMode,
    manual_mode_reason: manualModeReason,
  };
}

function recordOpportunityHistory(db: any, orgId: string, opportunityId: string, payload: any) {
  db.opportunityHistory.unshift({
    id: uid('hist'),
    tenant_id: orgId,
    opportunity_id: opportunityId,
    changed_at: payload.changed_at || nowIso(),
    actor_id: payload.actor_id || '',
    actor_name: payload.actor_name || '',
    event_type: payload.event_type,
    from_value: String(payload.from_value ?? ''),
    to_value: String(payload.to_value ?? ''),
    summary: payload.summary || '',
  });
}

function canAdvanceOpportunityStage(opportunity: any, payload: any) {
  if (!opportunity) return { ok: false, reason: 'Oportunidad no encontrada' };
  if (isClosedStage(String(opportunity.stage || ''))) return { ok: true, reason: '' };
  const lastActivityAt = String(opportunity.last_activity_at || '').trim();
  const hasRecentActivity = lastActivityAt ? daysSince(lastActivityAt) <= OPPORTUNITY_HEALTH_ACTIVITY_RECENT_DAYS : false;
  const nextStepChanged = Boolean(String(payload?.next_step || '').trim() && String(payload?.next_step_date || '').trim());
  if (hasRecentActivity || nextStepChanged) return { ok: true, reason: '' };
  return { ok: false, reason: 'Para avanzar etapa debe registrar actividad reciente o actualizar próximo paso y fecha.' };
}

function updateOpportunityActivitySnapshot(db: any, opportunityId: string) {
  const opportunity = db.opportunities.find((item: any) => item.id === opportunityId);
  if (!opportunity) return;
  const related = db.activities
    .filter((item: any) => item.opportunity_id === opportunityId)
    .sort((a: any, b: any) => new Date(b.created_at || b.due_date || 0).getTime() - new Date(a.created_at || a.due_date || 0).getTime());
  opportunity.last_activity_at = related[0]?.created_at || opportunity.last_activity_at || '';
}

function ensureCompany(db: any, orgId: string, payload: any) {
  const candidate = {
    tenant_id: orgId,
    name: payload?.name || payload?.company || payload?.razon_social || '',
    razon_social: payload?.razon_social || payload?.name || payload?.company || '',
    rut: payload?.rut || '',
    giro: payload?.giro || payload?.industry || '',
    industry: payload?.industry || '',
    website: payload?.website || '',
    phone: payload?.phone || '',
    email: payload?.email || '',
    address: payload?.address || '',
    region: payload?.region || '',
    comuna: payload?.comuna || '',
  };
  if (!String(candidate.name || '').trim()) {
    errorResponse('Empresa es obligatoria para la conversion', 400);
  }
  const dedupeKey = dedupeCompanyKey(candidate);
  let existing = db.companies.find((item: any) => item.tenant_id === orgId && item.dedupe_key === dedupeKey);
  if (existing) {
    Object.assign(existing, {
      ...candidate,
      normalized_name: normalizeKey(candidate.name),
      dedupe_key: dedupeKey,
    });
    return existing;
  }
  existing = {
    id: uid('company'),
    tenant_id: orgId,
    created_at: nowIso(),
    ...candidate,
    normalized_name: normalizeKey(candidate.name),
    dedupe_key: dedupeKey,
  };
  db.companies.unshift(existing);
  return existing;
}

function ensureContact(db: any, orgId: string, payload: any, company: any) {
  const candidate = {
    tenant_id: orgId,
    name: payload?.name || '',
    email: payload?.email || '',
    phone: payload?.phone || '',
    whatsapp: payload?.whatsapp || payload?.phone || '',
    preferred_channel: payload?.preferred_channel || 'whatsapp',
    position: payload?.position || 'Contacto comercial',
    company_id: company.id,
    company_name: company.name,
  };
  if (!String(candidate.name || '').trim()) {
    errorResponse('Nombre de contacto es obligatorio para la conversion', 400);
  }
  candidate['normalized_email'] = normalizeKey(candidate.email);
  candidate['normalized_name'] = normalizeKey(candidate.name);
  candidate['dedupe_key'] = dedupeContactKey(candidate);
  let existing = db.contacts.find((item: any) => item.tenant_id === orgId && item.dedupe_key === candidate['dedupe_key']);
  if (existing) {
    Object.assign(existing, candidate);
    return existing;
  }
  existing = {
    id: uid('contact'),
    created_at: nowIso(),
    ...candidate,
  };
  db.contacts.unshift(existing);
  return existing;
}

function qualifyLead(db: any, orgId: string, profile: any, leadId: string, payload: any) {
  const lead = db.leads.find((item: any) => item.id === leadId && item.tenant_id === orgId);
  if (!lead) errorResponse('Lead no encontrado', 404);
  if (lead.status === 'convertido') errorResponse('El lead ya fue convertido', 400);
  if (lead.status === 'descartado') errorResponse('El lead esta descartado', 400);
  const qualification = buildQualification(payload);
  lead.status = 'calificado';
  lead.owner_id = payload?.owner_id || lead.owner_id || profile.id;
  lead.owner_name = getProfileName(db, lead.owner_id) || profile.name;
  lead.qualification = qualification;
  lead.qualification_type = qualification.qualification_type;
  lead.updated_at = nowIso();
  writeDb(db);
  return lead;
}

function discardLead(db: any, orgId: string, leadId: string, payload: any) {
  const lead = db.leads.find((item: any) => item.id === leadId && item.tenant_id === orgId);
  if (!lead) errorResponse('Lead no encontrado', 404);
  const reason = String(payload?.discard_reason || payload?.reason || '').trim();
  if (!reason) errorResponse('Motivo de descarte es obligatorio', 400);
  lead.status = 'descartado';
  lead.discard_reason = reason;
  lead.updated_at = nowIso();
  writeDb(db);
  return lead;
}

function createOpportunityRecord(db: any, orgId: string, profile: any, payload: any) {
  const record = validateOpportunityPayload(db, payload, profile);
  const created = seedOpportunity({
    id: uid('opp'),
    tenant_id: orgId,
    created_at: nowIso(),
    updated_at: nowIso(),
    stage_entered_at: nowIso(),
    last_activity_at: '',
    ...record,
  });
  db.opportunities.unshift(created);
  recordOpportunityHistory(db, orgId, created.id, {
    event_type: 'stage_changed',
    actor_id: created.owner_id,
    actor_name: created.owner_name,
    from_value: '',
    to_value: created.stage,
    summary: `${created.owner_name} creo la oportunidad en ${created.stage}`,
  });
  recordOpportunityHistory(db, orgId, created.id, {
    event_type: 'probability_changed',
    actor_id: created.owner_id,
    actor_name: created.owner_name,
    from_value: '',
    to_value: created.probability,
    summary: `${created.owner_name} definio la probabilidad en ${created.probability}%`,
  });
  if (created.stage === 'Propuesta enviada') {
    createAutomaticFollowUp(db, orgId, created, created.owner_id, created.owner_name);
  }
  return created;
}

function convertLead(db: any, orgId: string, profile: any, leadId: string, payload: any) {
  const lead = db.leads.find((item: any) => item.id === leadId && item.tenant_id === orgId);
  if (!lead) errorResponse('Lead no encontrado', 404);
  if (!isQualifiedProspect(lead)) {
    errorResponse('Solo se puede convertir un lead calificado como prospecto', 400);
  }

  const company = ensureCompany(db, orgId, {
    name: payload?.company_name || payload?.company || lead.company,
    razon_social: payload?.company_razon_social || payload?.company_name || payload?.company || lead.company,
    rut: payload?.company_rut || '',
    giro: payload?.company_giro || '',
    industry: payload?.company_industry || '',
    phone: payload?.company_phone || '',
    email: payload?.company_email || '',
    address: payload?.company_address || '',
    region: payload?.company_region || '',
    comuna: payload?.company_comuna || '',
  });

  const contact = ensureContact(db, orgId, {
    name: lead.name,
    email: lead.email,
    phone: lead.phone,
    preferred_channel: payload?.preferred_channel || 'whatsapp',
    position: payload?.contact_position || 'Contacto comercial',
  }, company);

  const opportunity = createOpportunityRecord(db, orgId, profile, {
    name: payload?.opportunity_name || `Oportunidad ${company.name}`,
    account_id: company.id,
    account_name: company.name,
    contact_id: contact.id,
    contact_name: contact.name,
    owner_id: payload?.owner_id || lead.owner_id || profile.id,
    source_lead_id: lead.id,
    qualification_source: 'lead-conversion',
    monto_neto: Number(payload?.monto_neto || 0),
    exento: Boolean(payload?.exento),
    stage: payload?.stage || 'Prospecto',
    probability: Number(payload?.probability || STAGE_PROBABILITIES[payload?.stage || 'Prospecto']),
    close_date: payload?.close_date || dateOnly(shiftDays(new Date(), 30).toISOString()),
    next_step: payload?.next_step || 'Agendar reunion / demo',
    next_step_date: payload?.next_step_date || dateOnly(shiftDays(new Date(), 3).toISOString()),
    lost_reason: payload?.lost_reason || '',
  });

  lead.status = 'convertido';
  lead.converted_at = nowIso();
  lead.converted_company_id = company.id;
  lead.converted_contact_id = contact.id;
  lead.converted_opportunity_id = opportunity.id;
  lead.updated_at = nowIso();
  writeDb(db);

  return {
    lead,
    company,
    contact,
    opportunity,
  };
}

function createAutomaticFollowUp(db: any, orgId: string, opportunity: any, ownerId: string, ownerName: string) {
  const exists = db.activities.some((item: any) =>
    item.tenant_id === orgId
    && item.opportunity_id === opportunity.id
    && normalizeText(item.subject || '').includes('follow-up')
    && item.due_date === dateOnly(shiftDays(new Date(), 3).toISOString())
  );
  if (exists) return;
  const activity = {
    id: uid('activity'),
    tenant_id: orgId,
    type: 'tarea',
    subject: `Follow-up propuesta ${opportunity.account_name || opportunity.name}`,
    description: 'Seguimiento automatico a 3 dias de la propuesta enviada.',
    status: 'pendiente',
    due_date: dateOnly(shiftDays(new Date(), 3).toISOString()),
    created_at: nowIso(),
    owner_id: ownerId,
    owner_name: ownerName,
    company_id: opportunity.account_id,
    contact_id: opportunity.contact_id,
    opportunity_id: opportunity.id,
    related_entity_type: 'opportunity',
    related_entity_id: opportunity.id,
    related_to: opportunity.account_name || opportunity.name,
  };
  db.activities.unshift(activity);
  recordOpportunityHistory(db, orgId, opportunity.id, {
    event_type: 'activity_logged',
    actor_id: ownerId,
    actor_name: ownerName,
    summary: `Actividad registrada: ${activity.subject}`,
  });
}

function updateOpportunityStage(db: any, orgId: string, profile: any, opportunityId: string, payload: any) {
  const opportunity = db.opportunities.find((item: any) => item.id === opportunityId && item.tenant_id === orgId);
  if (!opportunity) errorResponse('Oportunidad no encontrada', 404);
  const nextStage = buildStageFromPayload(payload);
  const stageChanged = nextStage !== opportunity.stage;
  const lostReason = String(payload?.lost_reason || opportunity.lost_reason || '').trim();
  if (nextStage === 'Cerrado perdido' && !lostReason) {
    errorResponse('Motivo de negocio perdido es obligatorio', 400);
  }
  if (shouldRequireNextStep(nextStage)) {
    if (!String(payload?.next_step || opportunity.next_step || '').trim()) {
      errorResponse('Proximo paso es obligatorio', 400);
    }
    if (!String(payload?.next_step_date || opportunity.next_step_date || '').trim()) {
      errorResponse('Fecha de proximo paso es obligatoria', 400);
    }
  }
  if (stageChanged && !isClosedStage(nextStage)) {
    const check = canAdvanceOpportunityStage(opportunity, payload || {});
    if (!check.ok) errorResponse(check.reason, 400);
  }
  const previousStage = opportunity.stage;
  opportunity.stage = nextStage;
  opportunity.stage_entered_at = nowIso();
  opportunity.updated_at = nowIso();
  const previousNextStep = String(opportunity.next_step || '');
  const previousNextStepDate = String(opportunity.next_step_date || '');
  if (payload?.next_step) opportunity.next_step = payload.next_step;
  if (payload?.next_step_date) opportunity.next_step_date = payload.next_step_date;
  if (nextStage === 'Cerrado perdido') opportunity.lost_reason = lostReason;
  if (nextStage === 'Cerrado ganado') {
    opportunity.probability = 100;
  }
  if (nextStage === 'Cerrado perdido') {
    opportunity.probability = 0;
  }
  Object.assign(opportunity, calculateOpportunityMoney(opportunity));
  recordOpportunityHistory(db, orgId, opportunity.id, {
    event_type: 'stage_changed',
    actor_id: profile.id,
    actor_name: profile.name,
    from_value: previousStage,
    to_value: nextStage,
    summary: `${profile.name} movio la oportunidad de ${previousStage} a ${nextStage}`,
  });
  if ((payload?.next_step && String(payload.next_step) !== previousNextStep) || (payload?.next_step_date && String(payload.next_step_date) !== previousNextStepDate)) {
    recordOpportunityHistory(db, orgId, opportunity.id, {
      event_type: 'next_step_changed',
      actor_id: profile.id,
      actor_name: profile.name,
      from_value: `${previousNextStep} ${previousNextStepDate}`.trim(),
      to_value: `${opportunity.next_step || ''} ${opportunity.next_step_date || ''}`.trim(),
      summary: `${profile.name} actualizo el proximo paso`,
    });
  }
  if (nextStage === 'Propuesta enviada') {
    createAutomaticFollowUp(db, orgId, opportunity, opportunity.owner_id, opportunity.owner_name);
  }
  writeDb(db);
  return opportunity;
}

function updateOpportunityProbability(db: any, orgId: string, profile: any, opportunityId: string, payload: any) {
  const opportunity = db.opportunities.find((item: any) => item.id === opportunityId && item.tenant_id === orgId);
  if (!opportunity) errorResponse('Oportunidad no encontrada', 404);
  const nextProbability = Number(payload?.probability);
  if (Number.isNaN(nextProbability) || nextProbability < 0 || nextProbability > 100) {
    errorResponse('Probabilidad invalida', 400);
  }
  const previousProbability = Number(opportunity.probability || 0);
  opportunity.probability = nextProbability;
  opportunity.updated_at = nowIso();
  Object.assign(opportunity, calculateOpportunityMoney(opportunity));
  recordOpportunityHistory(db, orgId, opportunity.id, {
    event_type: 'probability_changed',
    actor_id: profile.id,
    actor_name: profile.name,
    from_value: previousProbability,
    to_value: nextProbability,
    summary: `${profile.name} actualizo la probabilidad de ${previousProbability}% a ${nextProbability}%`,
  });
  writeDb(db);
  return opportunity;
}

function createActivity(db: any, orgId: string, profile: any, payload: any) {
  if (!String(payload?.subject || '').trim()) {
    errorResponse('Asunto de actividad es obligatorio', 400);
  }
  const record = {
    id: uid('activity'),
    tenant_id: orgId,
    type: payload?.type || 'tarea',
    subject: payload?.subject,
    description: payload?.description || '',
    due_date: payload?.due_date || '',
    status: payload?.status || 'pendiente',
    company_id: payload?.company_id || '',
    contact_id: payload?.contact_id || '',
    opportunity_id: payload?.opportunity_id || '',
    related_entity_type: payload?.related_entity_type || (payload?.opportunity_id ? 'opportunity' : payload?.company_id ? 'company' : payload?.contact_id ? 'contact' : ''),
    related_entity_id: payload?.related_entity_id || payload?.opportunity_id || payload?.company_id || payload?.contact_id || '',
    related_to: payload?.related_to || '',
    owner_id: payload?.owner_id || profile.id,
    owner_name: getProfileName(db, payload?.owner_id || profile.id) || profile.name,
    created_at: nowIso(),
  };
  db.activities.unshift(record);
  if (record.opportunity_id) {
    updateOpportunityActivitySnapshot(db, record.opportunity_id);
    recordOpportunityHistory(db, orgId, record.opportunity_id, {
      event_type: 'activity_logged',
      actor_id: record.owner_id,
      actor_name: record.owner_name,
      summary: `Actividad registrada: ${record.subject}`,
    });
  }
  writeDb(db);
  return record;
}

function updateActivity(db: any, orgId: string, payload: any, activityId: string) {
  const activity = db.activities.find((item: any) => item.id === activityId && item.tenant_id === orgId);
  if (!activity) errorResponse('Actividad no encontrada', 404);
  Object.assign(activity, payload || {});
  if (activity.opportunity_id) {
    updateOpportunityActivitySnapshot(db, activity.opportunity_id);
  }
  writeDb(db);
  return activity;
}

function computeAlerts(db: any, orgId: string, params: any = {}) {
  const leads = getOrgRecords(db, 'leads', orgId);
  const opportunities = getOrgRecords(db, 'opportunities', orgId);
  const activities = getOrgRecords(db, 'activities', orgId);
  const openOpportunities = opportunities.filter((item: any) => !isClosedStage(item.stage));

  const alerts = [];

  leads.forEach((lead: any) => {
    if (lead.status === 'nuevo' && daysSince(lead.created_at) >= 1) {
      alerts.push({
        id: `lead-${lead.id}`,
        type: 'lead_sin_contacto_24h',
        severity: 'alta',
        entity_type: 'lead',
        entity_id: lead.id,
        title: `Lead sin gestionar: ${lead.name}`,
        description: `Han pasado más de 24 horas sin contacto para ${lead.name}.`,
      });
    }
  });

  openOpportunities.forEach((opportunity: any) => {
    const lastActivity = activities
      .filter((item: any) => item.opportunity_id === opportunity.id)
      .sort((a: any, b: any) => new Date(b.created_at || b.due_date || 0).getTime() - new Date(a.created_at || a.due_date || 0).getTime())[0];

    if (!String(opportunity.next_step || '').trim() || !String(opportunity.next_step_date || '').trim()) {
      alerts.push({
        id: `opp-next-step-${opportunity.id}`,
        type: 'oportunidad_sin_proximo_paso',
        severity: 'alta',
        entity_type: 'opportunity',
        entity_id: opportunity.id,
        title: `Oportunidad sin próximo paso: ${opportunity.name}`,
        description: 'Toda oportunidad abierta debe tener próximo paso y fecha.',
      });
    }

    if (String(opportunity.next_step_date || '').trim() && opportunity.next_step_date < dateOnly(nowIso())) {
      alerts.push({
        id: `opp-followup-${opportunity.id}`,
        type: 'seguimiento_vencido',
        severity: 'media',
        entity_type: 'opportunity',
        entity_id: opportunity.id,
        title: `Seguimiento vencido: ${opportunity.name}`,
        description: `El próximo paso venció el ${opportunity.next_step_date}.`,
      });
    }

    if (!lastActivity || daysSince(lastActivity.created_at || lastActivity.due_date) >= 7) {
      alerts.push({
        id: `opp-no-activity-${opportunity.id}`,
        type: 'oportunidad_sin_actividad',
        severity: 'media',
        entity_type: 'opportunity',
        entity_id: opportunity.id,
        title: `Sin actividad reciente: ${opportunity.name}`,
        description: 'La oportunidad no registra actividad hace 7 días o más.',
      });
    }

    const slaDays = getStageSlaDays(db, orgId, opportunity.stage);
    if (daysSince(opportunity.stage_entered_at || opportunity.updated_at || opportunity.created_at) > slaDays) {
      alerts.push({
        id: `opp-stale-${opportunity.id}`,
        type: 'oportunidad_estancada',
        severity: 'alta',
        entity_type: 'opportunity',
        entity_id: opportunity.id,
        title: `Oportunidad estancada: ${opportunity.name}`,
        description: `La oportunidad excede el SLA de etapa (${slaDays} días).`,
      });
    }

    const hydrated = hydrateOpportunity(opportunity, db);
    if (Number(hydrated?.health_score || 0) < OPPORTUNITY_HEALTH_THRESHOLDS.healthy) {
      alerts.push({
        id: `opp-health-${opportunity.id}`,
        type: 'oportunidad_en_riesgo',
        severity: hydrated.health_band === 'alto_riesgo' ? 'alta' : 'media',
        entity_type: 'opportunity',
        entity_id: opportunity.id,
        title: `Oportunidad en riesgo: ${opportunity.name}`,
        description: hydrated.health_recommendation || 'Revisar oportunidad',
        score: hydrated.health_score,
        band: hydrated.health_band,
      });
    }
  });

  const filtered = applyDateFilter(alerts, params.date_from, params.date_to);
  return filtered;
}

function computeSellerPerformance(db: any, orgId: string, params: any = {}) {
  const profiles = db.profiles.filter((item: any) => item.memberships.some((membership: any) => membership.org_id === orgId));
  const leads = applyDateFilter(getOrgRecords(db, 'leads', orgId), params.date_from, params.date_to);
  const opportunities = applyDateFilter(getOrgRecords(db, 'opportunities', orgId), params.date_from, params.date_to);
  const activities = applyDateFilter(getOrgRecords(db, 'activities', orgId), params.date_from, params.date_to);

  return profiles
    .filter((profile: any) => ['admin', 'manager', 'vendedor'].includes(profile.role || ''))
    .map((profile: any) => {
      const sellerLeads = leads.filter((item: any) => item.owner_id === profile.id);
      const sellerOpps = opportunities.filter((item: any) => item.owner_id === profile.id);
      const sellerActivities = activities.filter((item: any) => item.owner_id === profile.id);
      const sellerWon = sellerOpps.filter((item: any) => item.stage === 'Cerrado ganado');
      const sellerQualified = sellerLeads.filter((item: any) => item.status === 'calificado' || item.status === 'convertido');
      return {
        seller_id: profile.id,
        seller_name: profile.name,
        role: profile.role,
        leads_managed: sellerLeads.length,
        opportunities_created: sellerOpps.length,
        closed_won_count: sellerWon.length,
        conversion_rate: sellerLeads.length ? Math.round((sellerWon.length / sellerLeads.length) * 100) : 0,
        qualification_rate: sellerLeads.length ? Math.round((sellerQualified.length / sellerLeads.length) * 100) : 0,
        amount_sold: sellerWon.reduce((sum: number, item: any) => sum + Number(item.amount || 0), 0),
        activities_done: sellerActivities.length,
      };
    })
    .sort((a: any, b: any) => b.amount_sold - a.amount_sold || b.closed_won_count - a.closed_won_count);
}

function buildDashboardKpis(db: any, orgId: string, params: any = {}) {
  const leads = applyDateFilter(getOrgRecords(db, 'leads', orgId), params.date_from, params.date_to);
  const metrics = computeCommercialMetrics(db, orgId, params);
  const opportunities = metrics.opportunities_in_range;
  const activities = applyDateFilter(getOrgRecords(db, 'activities', orgId), params.date_from, params.date_to);
  const alerts = computeAlerts(db, orgId, params);
  const openOpportunities = metrics.open_opportunities;
  const wonOpportunities = metrics.won_in_range;
  const qualifiedLeads = leads.filter((item: any) => item.status === 'calificado' || item.status === 'convertido');
  const prospects = leads.filter((item: any) => item.qualification?.qualification_type === 'Prospecto' && item.status !== 'descartado');
  const clients = new Set(wonOpportunities.map((item: any) => item.account_id).filter(Boolean));
  const monthlyForecast = metrics.forecast_periodo;
  const weightedPipeline = metrics.pipeline_ponderado;
  const pipelineValue = metrics.pipeline_abierto;
  const pendingActivities = activities.filter((item: any) => item.status === 'pendiente').length;
  const staleAlerts = alerts.filter((item: any) => item.type === 'oportunidad_estancada').length;
  const overdueFollowUps = alerts.filter((item: any) => item.type === 'seguimiento_vencido').length;
  const lostReasons = opportunities.filter((item: any) => item.stage === 'Cerrado perdido' && item.lost_reason);
  const oppsAtRisk = alerts.filter((item: any) => item.type === 'oportunidad_en_riesgo').length;
  const oppsNoNextStep = alerts.filter((item: any) => item.type === 'oportunidad_sin_proximo_paso').length;
  const leadsOverdue = alerts.filter((item: any) => item.type === 'lead_sin_contacto_24h').length;

  return {
    leads_count: leads.length,
    prospects_count: prospects.length,
    clients_count: clients.size,
    sales_count: wonOpportunities.length,
    qualification_rate: leads.length ? Math.round((qualifiedLeads.length / leads.length) * 100) : 0,
    close_rate: opportunities.length ? Math.round((wonOpportunities.length / opportunities.length) * 100) : 0,
    conversion_rate: metrics.conversion_rate,
    pipeline_value: pipelineValue,
    pipeline_total: metrics.pipeline_total,
    weighted_pipeline: weightedPipeline,
    monthly_forecast: monthlyForecast,
    pending_activities: pendingActivities,
    alerts_count: alerts.length,
    stale_opportunities: staleAlerts,
    overdue_followups: overdueFollowUps,
    lost_deals_count: lostReasons.length,
    active_opportunities: openOpportunities.length,
    won_deals: wonOpportunities.length,
    opps_at_risk: oppsAtRisk,
    opps_no_next_step: oppsNoNextStep,
    leads_overdue_24h: leadsOverdue,
    plata_en_la_calle: opportunities
      .filter((item: any) => ['Propuesta enviada', 'Negociacion'].includes(item.stage))
      .reduce((sum: number, item: any) => sum + Number(item.amount || 0), 0),
    alertas_inactividad: staleAlerts,
  };
}

function groupCount(items: any[], field: string) {
  const groups = new Map<string, number>();
  items.forEach((item) => {
    const key = item[field] || 'Sin dato';
    groups.set(key, (groups.get(key) || 0) + 1);
  });
  return Array.from(groups.entries()).map(([key, count]) => ({ _id: key, count }));
}

function groupTotal(items: any[], field: string, totalField: string) {
  const groups = new Map<string, number>();
  items.forEach((item) => {
    const key = item[field] || 'Sin dato';
    groups.set(key, (groups.get(key) || 0) + Number(item[totalField] || 0));
  });
  return Array.from(groups.entries()).map(([key, total]) => ({ _id: key, total }));
}

function resolveMetricsRange(params: any) {
  if (params?.date_from || params?.date_to) {
    return { from: String(params?.date_from || ''), to: String(params?.date_to || '') };
  }
  return getCurrentMonthRange();
}

function shouldCountOpenOpportunityInRange(opportunity: any, from: string, to: string) {
  const closeDate = String(opportunity?.close_date || '').trim();
  if (!from && !to) return true;
  if (!closeDate) return true;
  return inDateRange(closeDate, from, to);
}

function computeCommercialMetrics(db: any, orgId: string, params: any = {}) {
  const { from, to } = resolveMetricsRange(params);
  const allOpportunities = getOrgRecords(db, 'opportunities', orgId);
  const opportunitiesInRange = applyDateFilter(allOpportunities, params.date_from, params.date_to);

  const wonInRange = allOpportunities.filter((item: any) => item.stage === 'Cerrado ganado' && inDateRange(item.close_date, from, to));
  const lostInRange = allOpportunities.filter((item: any) => item.stage === 'Cerrado perdido' && inDateRange(item.close_date, from, to));
  const openOpportunities = allOpportunities.filter((item: any) => !isClosedStage(item.stage) && shouldCountOpenOpportunityInRange(item, from, to));

  const pipelineAbierto = openOpportunities.reduce((sum: number, item: any) => sum + Number(item.amount || 0), 0);
  const pipelinePonderado = openOpportunities.reduce((sum: number, item: any) => sum + Math.round(Number(item.amount || 0) * (Number(item.probability || 0) / 100)), 0);
  const ganadoPeriodo = wonInRange.reduce((sum: number, item: any) => sum + Number(item.amount || 0), 0);
  const perdidoPeriodo = lostInRange.reduce((sum: number, item: any) => sum + Number(item.amount || 0), 0);
  const pipelineTotal = pipelineAbierto + ganadoPeriodo;
  const forecastPeriodo = pipelinePonderado + ganadoPeriodo;

  const leadsInRange = applyDateFilter(getOrgRecords(db, 'leads', orgId), params.date_from, params.date_to);
  const leadsConvertidos = leadsInRange.filter((item: any) => item.status === 'convertido');
  const winRateDenom = wonInRange.length + lostInRange.length;
  const winRate = winRateDenom ? Math.round((wonInRange.length / winRateDenom) * 100) : 0;

  return {
    range: { from, to },
    opportunities_in_range: opportunitiesInRange,
    open_opportunities: openOpportunities,
    won_in_range: wonInRange,
    lost_in_range: lostInRange,
    pipeline_abierto: pipelineAbierto,
    pipeline_total: pipelineTotal,
    pipeline_ponderado: pipelinePonderado,
    ganado_periodo: ganadoPeriodo,
    perdido_periodo: perdidoPeriodo,
    forecast_periodo: forecastPeriodo,
    conversion_rate: leadsInRange.length ? Math.round((leadsConvertidos.length / leadsInRange.length) * 100) : 0,
    win_rate: winRate,
  };
}

function buildDashboardCharts(db: any, orgId: string, params: any = {}) {
  const leads = applyDateFilter(getOrgRecords(db, 'leads', orgId), params.date_from, params.date_to);
  const opportunities = applyDateFilter(getOrgRecords(db, 'opportunities', orgId), params.date_from, params.date_to);
  const sellerPerformance = computeSellerPerformance(db, orgId, params);
  const alerts = computeAlerts(db, orgId, params);
  return {
    pipeline_by_stage: groupTotal(opportunities.filter((item: any) => !isClosedStage(item.stage)), 'stage', 'amount'),
    leads_by_source: groupCount(leads, 'source'),
    leads_by_status: groupCount(leads, 'status'),
    seller_performance: sellerPerformance.map((item: any) => ({
      _id: item.seller_name,
      amount_sold: item.amount_sold,
      closed_won_count: item.closed_won_count,
      opportunities_created: item.opportunities_created,
    })),
    alerts_by_type: groupCount(alerts, 'type'),
  };
}

function buildSalesReport(db: any, orgId: string, params: any = {}) {
  const metrics = computeCommercialMetrics(db, orgId, params);
  const opportunities = metrics.opportunities_in_range;
  const won = metrics.won_in_range;
  const lost = metrics.lost_in_range;
  return {
    total_revenue: metrics.ganado_periodo,
    won_count: won.length,
    lost_count: lost.length,
    pipeline_abierto: metrics.pipeline_abierto,
    pipeline_total: metrics.pipeline_total,
    weighted_pipeline: metrics.pipeline_ponderado,
    monthly_forecast: metrics.forecast_periodo,
    ganado_periodo: metrics.ganado_periodo,
    perdido_periodo: metrics.perdido_periodo,
    win_rate: metrics.win_rate,
    conversion_rate: metrics.conversion_rate,
    by_stage: groupTotal(opportunities, 'stage', 'amount'),
    lost_reasons: groupCount(lost, 'lost_reason'),
    seller_performance: computeSellerPerformance(db, orgId, params),
    alerts: computeAlerts(db, orgId, params),
  };
}

export function ensureLocalProfile(authUser: any, preferredName?: string) {
  const db = readDb();
  let profile = db.profiles.find((item: any) => item.id === authUser.id);
  const name = preferredName || authUser.user_metadata?.name || authUser.user_metadata?.full_name || fullNameFromEmail(authUser.email || '');
  if (!profile) {
    const orgId = 'org_demo';
    cloneOrgSupportData(db, orgId);
    profile = {
      id: authUser.id,
      name,
      email: authUser.email,
      role: SHARED_DEMO_EMAILS.has(String(authUser.email || '').toLowerCase()) ? 'manager' : 'admin',
      memberships: [{ org_id: orgId, role: SHARED_DEMO_EMAILS.has(String(authUser.email || '').toLowerCase()) ? 'manager' : 'admin' }],
      created_at: nowIso(),
    };
    db.profiles.push(profile);
  } else {
    profile.name = name || profile.name;
    profile.email = authUser.email || profile.email;
  }
  writeDb(db);
  return buildSessionUser(db, profile);
}

export function getLocalSessionUser(userId: string) {
  const db = readDb();
  const profile = db.profiles.find((item: any) => item.id === userId);
  if (!profile) return null;
  return buildSessionUser(db, profile);
}

export async function acceptInvitationForUser(token: string, authUser: any, preferredName?: string) {
  const db = readDb();
  const invitation = db.invitations.find((item: any) => item.token === token && item.status === 'pending');
  if (!invitation) {
    throw new Error('Invitacion invalida o expirada');
  }
  let profile = db.profiles.find((item: any) => item.id === authUser.id);
  if (!profile) {
    profile = {
      id: authUser.id,
      name: preferredName || authUser.user_metadata?.name || authUser.user_metadata?.full_name || fullNameFromEmail(invitation.email),
      email: invitation.email,
      role: invitation.role || 'viewer',
      memberships: [],
      created_at: nowIso(),
    };
    db.profiles.push(profile);
  }
  const hasMembership = profile.memberships.some((membership: any) => membership.org_id === invitation.org_id);
  if (!hasMembership) {
    profile.memberships.push({ org_id: invitation.org_id, role: invitation.role || 'viewer' });
  }
  cloneOrgSupportData(db, invitation.org_id);
  profile.name = preferredName || profile.name;
  profile.email = invitation.email;
  invitation.status = 'accepted';
  invitation.accepted_at = nowIso();
  invitation.accepted_by = profile.id;
  writeDb(db);
  return buildSessionUser(db, profile);
}

async function currentAuthUser() {
  if (typeof window !== 'undefined') {
    const localDemoUser = window.localStorage.getItem('conectacrm.local.auth.user');
    if (localDemoUser) {
      try {
        return JSON.parse(localDemoUser);
      } catch {
        window.localStorage.removeItem('conectacrm.local.auth.user');
      }
    }
    const raw = window.localStorage.getItem('conectacrm.e2e.user');
    if (raw) {
      try {
        return JSON.parse(raw);
      } catch {
        window.localStorage.removeItem('conectacrm.e2e.user');
      }
    }
  }
  const result = await supabase?.auth.getUser();
  return result?.data?.user || null;
}

function errorResponse(detail: string, status = 400) {
  const error: any = new Error(detail);
  error.response = { data: { detail }, status };
  error.status = status;
  throw error;
}

function requireCurrentOrg(profile: any) {
  const orgId = getCurrentOrgId(profile);
  if (!orgId) {
    errorResponse('No hay una organizacion seleccionada', 400);
  }
  return orgId;
}

export async function handleLocalApi(method: string, url: string, payload?: any, config?: any) {
  const db = readDb();
  const params = config?.params || {};
  const authUser = await currentAuthUser();
  const profile = authUser ? requireUserProfile(db, authUser.id) : null;
  const orgId = profile ? requireCurrentOrg(profile) : null;
  const normalizedUrl = url.replace(/^\/+/, '/');

  if (normalizedUrl === '/invitations/' && method === 'GET') {
    errorResponse('Invitacion invalida o expirada', 404);
  }

  if (normalizedUrl.startsWith('/invitations/') && method === 'GET') {
    const token = normalizedUrl.split('/').pop();
    const invitation = db.invitations.find((item: any) => item.token === token && item.status === 'pending');
    if (!invitation) {
      errorResponse('Invitacion invalida o expirada', 404);
    }
    return { data: invitation, status: 200 };
  }

  if (normalizedUrl === '/auth/register-invite' && method === 'POST') {
    const invitation = db.invitations.find((item: any) => item.token === payload?.token && item.status === 'pending');
    if (!invitation) {
      errorResponse('Invitacion invalida o expirada', 404);
    }
    let user = authUser;
    if (!user) {
      const signUpResult = await supabase?.auth.signUp({
        email: invitation.email,
        password: payload?.password,
        options: { data: { name: payload?.name } },
      });
      if (signUpResult?.error) {
        errorResponse(signUpResult.error.message, 400);
      }
      const signInResult = await supabase?.auth.signInWithPassword({
        email: invitation.email,
        password: payload?.password,
      });
      if (signInResult?.error) {
        errorResponse(signInResult.error.message, 400);
      }
      user = signInResult?.data?.user || signUpResult?.data?.user || null;
    }
    if (!user) {
      errorResponse('No fue posible crear la sesion local', 400);
    }
    const sessionUser = await acceptInvitationForUser(payload?.token, user, payload?.name);
    return { data: sessionUser, status: 200 };
  }

  if (!profile || !orgId) {
    errorResponse('No autenticado', 401);
  }

  if (normalizedUrl === '/settings/users' && method === 'GET') {
    const users = db.profiles
      .filter((item: any) => item.memberships.some((membership: any) => membership.org_id === orgId))
      .map((item: any) => {
        const membership = item.memberships.find((value: any) => value.org_id === orgId);
        return { id: item.id, name: item.name, email: item.email, role: membership?.role || 'viewer' };
      });
    return { data: users, status: 200 };
  }

  if (normalizedUrl === '/settings/invitations' && method === 'GET') {
    return { data: db.invitations.filter((item: any) => item.org_id === orgId), status: 200 };
  }

  if (normalizedUrl === '/settings/invite' && method === 'POST') {
    const token = uid('invite');
    const invitation = {
      id: uid('inv'),
      token,
      org_id: orgId,
      org_name: db.organizations.find((item: any) => item.id === orgId)?.name || 'Organizacion',
      email: String(payload?.email || '').toLowerCase(),
      role: payload?.role || 'viewer',
      status: 'pending',
      created_at: nowIso(),
      invite_link: typeof window !== 'undefined' ? `${window.location.origin}/invite/${token}` : `/invite/${token}`,
    };
    db.invitations.unshift(invitation);
    writeDb(db);
    return { data: invitation, status: 200 };
  }

  if (normalizedUrl.startsWith('/settings/invitations/') && method === 'DELETE') {
    const invitationId = normalizedUrl.split('/').pop();
    db.invitations = db.invitations.filter((item: any) => item.id !== invitationId);
    writeDb(db);
    return { data: { success: true }, status: 200 };
  }

  if (normalizedUrl === '/roles' && method === 'GET') {
    cloneOrgSupportData(db, orgId);
    writeDb(db);
    return { data: getOrgRecords(db, 'roles', orgId), status: 200 };
  }

  if (normalizedUrl === '/pipeline-stages' && method === 'GET') {
    cloneOrgSupportData(db, orgId);
    writeDb(db);
    return { data: getOrgRecords(db, 'pipelineStages', orgId).sort((a: any, b: any) => a.order - b.order), status: 200 };
  }

  if (normalizedUrl === '/organizations' && method === 'GET') {
    const organizations = profile.memberships
      .map((membership: any) => {
        const org = db.organizations.find((item: any) => item.id === membership.org_id);
        return org ? { ...org, role: membership.role } : null;
      })
      .filter(Boolean);
    return { data: organizations, status: 200 };
  }

  if (normalizedUrl.startsWith('/organizations/') && method === 'PUT') {
    const targetOrgId = normalizedUrl.split('/').pop();
    const organization = db.organizations.find((item: any) => item.id === targetOrgId);
    if (!organization) errorResponse('Organizacion no encontrada', 404);
    Object.assign(organization, payload || {});
    writeDb(db);
    return { data: organization, status: 200 };
  }

  if (normalizedUrl === '/dashboard/kpis' && method === 'GET') {
    return { data: buildDashboardKpis(db, orgId, params), status: 200 };
  }

  if (normalizedUrl === '/dashboard/charts' && method === 'GET') {
    return { data: buildDashboardCharts(db, orgId, params), status: 200 };
  }

  if (normalizedUrl === '/dashboard/alerts' && method === 'GET') {
    return { data: computeAlerts(db, orgId, params), status: 200 };
  }

  if (normalizedUrl === '/reports/campaigns' && method === 'GET') {
    return { data: getOrgRecords(db, 'campaigns', orgId), status: 200 };
  }

  if (normalizedUrl === '/reports/sales' && method === 'GET') {
    return { data: buildSalesReport(db, orgId, params), status: 200 };
  }

  if (normalizedUrl === '/reports/performance' && method === 'GET') {
    return { data: computeSellerPerformance(db, orgId, params), status: 200 };
  }

  if (normalizedUrl === '/chile/regiones' && method === 'GET') {
    return { data: REGIONES, status: 200 };
  }

  if (normalizedUrl === '/chile/giros' && method === 'GET') {
    return { data: GIROS, status: 200 };
  }

  if (normalizedUrl === '/leads' && method === 'GET') {
    const records = getOrgRecords(db, 'leads', orgId)
      .filter((item: any) => matchesSearch(item, params.search || '', ['name', 'email', 'company', 'discard_reason']))
      .filter((item: any) => !params.status || item.status === params.status)
      .sort((a: any, b: any) => new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime());
    return { data: records, status: 200 };
  }

  if (normalizedUrl === '/leads' && method === 'POST') {
    const record = validateLeadPayload({
      id: uid('lead'),
      tenant_id: orgId,
      created_at: nowIso(),
      updated_at: nowIso(),
      owner_id: payload?.owner_id || profile.id,
      owner_name: getProfileName(db, payload?.owner_id || profile.id) || profile.name,
      source: payload?.source || 'web',
      status: payload?.status || 'nuevo',
      qualification: payload?.qualification || buildQualification({}),
      qualification_type: payload?.qualification?.qualification_type || 'Lead',
      ...payload,
    });
    db.leads.unshift(record);
    writeDb(db);
    return { data: record, status: 201 };
  }

  if (
    normalizedUrl.startsWith('/leads/')
    && method === 'GET'
    && !normalizedUrl.endsWith('/qualify')
    && !normalizedUrl.endsWith('/discard')
    && !normalizedUrl.endsWith('/convert')
    && normalizedUrl !== '/leads/export/csv'
    && normalizedUrl !== '/leads/import/csv'
  ) {
    const recordId = normalizedUrl.split('/').pop();
    const record = db.leads.find((item: any) => item.id === recordId && item.tenant_id === orgId);
    if (!record) errorResponse('Lead no encontrado', 404);
    return { data: record, status: 200 };
  }

  if (normalizedUrl.startsWith('/leads/') && normalizedUrl.endsWith('/qualify') && method === 'POST') {
    const recordId = normalizedUrl.split('/')[2];
    return { data: qualifyLead(db, orgId, profile, recordId, payload || {}), status: 200 };
  }

  if (normalizedUrl.startsWith('/leads/') && normalizedUrl.endsWith('/discard') && method === 'POST') {
    const recordId = normalizedUrl.split('/')[2];
    return { data: discardLead(db, orgId, recordId, payload || {}), status: 200 };
  }

  if (normalizedUrl.startsWith('/leads/') && normalizedUrl.endsWith('/convert') && method === 'POST') {
    const recordId = normalizedUrl.split('/')[2];
    return { data: convertLead(db, orgId, profile, recordId, payload || {}), status: 200 };
  }

  if (normalizedUrl.startsWith('/leads/') && method === 'PUT') {
    const recordId = normalizedUrl.split('/').pop();
    const record = db.leads.find((item: any) => item.id === recordId && item.tenant_id === orgId);
    if (!record) errorResponse('Lead no encontrado', 404);
    Object.assign(record, validateLeadPayload({ ...record, ...payload, updated_at: nowIso() }, record), { updated_at: nowIso() });
    writeDb(db);
    return { data: record, status: 200 };
  }

  if (normalizedUrl.startsWith('/leads/') && method === 'DELETE') {
    const recordId = normalizedUrl.split('/').pop();
    db.leads = db.leads.filter((item: any) => !(item.id === recordId && item.tenant_id === orgId));
    writeDb(db);
    return { data: { success: true }, status: 200 };
  }

  if (normalizedUrl === '/leads/export/csv' && method === 'GET') {
    return { data: toBlob(rowsToCsv(getOrgRecords(db, 'leads', orgId)), 'text/csv;charset=utf-8'), status: 200 };
  }

  if (normalizedUrl === '/leads/import/csv' && method === 'POST') {
    const file = payload?.get?.('file');
    const text = file ? await file.text() : '';
    const rows = parseCsv(text);
    rows.forEach((row) => {
      db.leads.push(validateLeadPayload({
        id: uid('lead'),
        tenant_id: orgId,
        name: row.name || row.Nombre || '',
        email: row.email || row.Email || '',
        phone: row.phone || row.Telefono || '',
        company: row.company || row.Empresa || '',
        source: row.source || 'web',
        status: row.status || 'nuevo',
        owner_id: profile.id,
        owner_name: profile.name,
        created_at: nowIso(),
        updated_at: nowIso(),
        qualification: buildQualification({}),
      }));
    });
    writeDb(db);
    return { data: { count: rows.length, errors: [] }, status: 200 };
  }

  if (normalizedUrl === '/companies' && method === 'GET') {
    const records = getOrgRecords(db, 'companies', orgId).filter((item: any) => matchesSearch(item, params.search || '', ['name', 'razon_social', 'rut', 'giro', 'industry']));
    return { data: records, status: 200 };
  }

  if (normalizedUrl.startsWith('/companies/') && method === 'GET' && normalizedUrl !== '/companies/export/csv') {
    const recordId = normalizedUrl.split('/').pop();
    const record = db.companies.find((item: any) => item.id === recordId && item.tenant_id === orgId);
    if (!record) errorResponse('Empresa no encontrada', 404);
    return { data: record, status: 200 };
  }

  if (normalizedUrl === '/companies' && method === 'POST') {
    const record = ensureCompany(db, orgId, payload || {});
    writeDb(db);
    return { data: record, status: 201 };
  }

  if (normalizedUrl.startsWith('/companies/') && method === 'PUT') {
    const recordId = normalizedUrl.split('/').pop();
    const record = db.companies.find((item: any) => item.id === recordId && item.tenant_id === orgId);
    if (!record) errorResponse('Empresa no encontrada', 404);
    const dedupeKey = dedupeCompanyKey({ ...record, ...(payload || {}) });
    const duplicate = db.companies.find((item: any) => item.tenant_id === orgId && item.id !== recordId && item.dedupe_key === dedupeKey);
    if (duplicate) errorResponse('Ya existe una empresa equivalente en esta organizacion', 400);
    Object.assign(record, payload || {}, { dedupe_key: dedupeKey, normalized_name: normalizeKey(payload?.name || record.name) });
    writeDb(db);
    return { data: record, status: 200 };
  }

  if (normalizedUrl.startsWith('/companies/') && method === 'DELETE') {
    const recordId = normalizedUrl.split('/').pop();
    db.companies = db.companies.filter((item: any) => !(item.id === recordId && item.tenant_id === orgId));
    db.contacts = db.contacts.filter((item: any) => item.company_id !== recordId);
    writeDb(db);
    return { data: { success: true }, status: 200 };
  }

  if (normalizedUrl === '/companies/export/csv' && method === 'GET') {
    return { data: toBlob(rowsToCsv(getOrgRecords(db, 'companies', orgId)), 'text/csv;charset=utf-8'), status: 200 };
  }

  if (normalizedUrl === '/contacts' && method === 'GET') {
    const records = getOrgRecords(db, 'contacts', orgId).filter((item: any) => matchesSearch(item, params.search || '', ['name', 'email', 'phone', 'position', 'company_name']));
    return { data: records, status: 200 };
  }

  if (normalizedUrl === '/contacts' && method === 'POST') {
    const company = payload?.company_id ? db.companies.find((item: any) => item.id === payload.company_id && item.tenant_id === orgId) : null;
    const candidate = {
      id: uid('contact'),
      tenant_id: orgId,
      created_at: nowIso(),
      name: payload?.name,
      email: payload?.email || '',
      phone: payload?.phone || '',
      whatsapp: payload?.whatsapp || payload?.phone || '',
      preferred_channel: payload?.preferred_channel || 'whatsapp',
      position: payload?.position || '',
      company_id: company?.id || payload?.company_id || '',
      company_name: company?.name || payload?.company_name || '',
      normalized_email: normalizeKey(payload?.email || ''),
      normalized_name: normalizeKey(payload?.name || ''),
    };
    candidate['dedupe_key'] = dedupeContactKey(candidate);
    const duplicate = db.contacts.find((item: any) => item.tenant_id === orgId && item.dedupe_key === candidate['dedupe_key']);
    if (duplicate) errorResponse('Ya existe un contacto equivalente en esta organizacion', 400);
    db.contacts.unshift(candidate);
    writeDb(db);
    return { data: candidate, status: 201 };
  }

  if (normalizedUrl.startsWith('/contacts/') && method === 'PUT') {
    const recordId = normalizedUrl.split('/').pop();
    const record = db.contacts.find((item: any) => item.id === recordId && item.tenant_id === orgId);
    if (!record) errorResponse('Contacto no encontrado', 404);
    const company = payload?.company_id ? db.companies.find((item: any) => item.id === payload.company_id && item.tenant_id === orgId) : null;
    const updated = {
      ...record,
      ...(payload || {}),
      company_name: company?.name || payload?.company_name || record.company_name || '',
      normalized_email: normalizeKey(payload?.email || record.email || ''),
      normalized_name: normalizeKey(payload?.name || record.name || ''),
    };
    updated['dedupe_key'] = dedupeContactKey(updated);
    const duplicate = db.contacts.find((item: any) => item.tenant_id === orgId && item.id !== recordId && item.dedupe_key === updated['dedupe_key']);
    if (duplicate) errorResponse('Ya existe un contacto equivalente en esta organizacion', 400);
    Object.assign(record, updated);
    writeDb(db);
    return { data: record, status: 200 };
  }

  if (normalizedUrl.startsWith('/contacts/') && method === 'DELETE') {
    const recordId = normalizedUrl.split('/').pop();
    db.contacts = db.contacts.filter((item: any) => !(item.id === recordId && item.tenant_id === orgId));
    writeDb(db);
    return { data: { success: true }, status: 200 };
  }

  if (normalizedUrl === '/contacts/export/csv' && method === 'GET') {
    return { data: toBlob(rowsToCsv(getOrgRecords(db, 'contacts', orgId)), 'text/csv;charset=utf-8'), status: 200 };
  }

  if (normalizedUrl === '/opportunities' && method === 'GET') {
    let records = getOrgRecords(db, 'opportunities', orgId).map((item: any) => hydrateOpportunity(item, db));
    if (params.search) {
      records = records.filter((item: any) => matchesSearch(item, params.search, ['name', 'stage', 'account_name', 'contact_name', 'owner_name']));
    }
    if (params.stage) {
      records = records.filter((item: any) => item.stage === params.stage);
    }
    return { data: records, status: 200 };
  }

  if (normalizedUrl === '/opportunities' && method === 'POST') {
    const record = createOpportunityRecord(db, orgId, profile, payload || {});
    writeDb(db);
    return { data: hydrateOpportunity(record, db), status: 201 };
  }

  if (normalizedUrl.startsWith('/opportunities/') && normalizedUrl.endsWith('/history') && method === 'GET') {
    const recordId = normalizedUrl.split('/')[2];
    const history = db.opportunityHistory
      .filter((item: any) => item.tenant_id === orgId && item.opportunity_id === recordId)
      .sort((a: any, b: any) => new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime());
    return { data: history, status: 200 };
  }

  if (normalizedUrl.startsWith('/opportunities/') && normalizedUrl.endsWith('/stage') && method === 'PATCH') {
    const recordId = normalizedUrl.split('/')[2];
    return { data: hydrateOpportunity(updateOpportunityStage(db, orgId, profile, recordId, payload || {}), db), status: 200 };
  }

  if (normalizedUrl.startsWith('/opportunities/') && normalizedUrl.endsWith('/probability') && method === 'PATCH') {
    const recordId = normalizedUrl.split('/')[2];
    return { data: hydrateOpportunity(updateOpportunityProbability(db, orgId, profile, recordId, payload || {}), db), status: 200 };
  }

  if (normalizedUrl.startsWith('/opportunities/') && method === 'GET' && normalizedUrl !== '/opportunities/export/csv') {
    const recordId = normalizedUrl.split('/').pop();
    const record = db.opportunities.find((item: any) => item.id === recordId && item.tenant_id === orgId);
    if (!record) errorResponse('Oportunidad no encontrada', 404);
    return { data: hydrateOpportunity(record, db), status: 200 };
  }

  if (normalizedUrl.startsWith('/opportunities/') && method === 'PUT') {
    const recordId = normalizedUrl.split('/').pop();
    const record = db.opportunities.find((item: any) => item.id === recordId && item.tenant_id === orgId);
    if (!record) errorResponse('Oportunidad no encontrada', 404);
    const previousStage = record.stage;
    const previousProbability = record.probability;
    const updated = validateOpportunityPayload(db, payload || {}, profile, record);
    Object.assign(record, {
      ...record,
      ...updated,
      updated_at: nowIso(),
    });
    if (record.stage !== previousStage) {
      record.stage_entered_at = nowIso();
      recordOpportunityHistory(db, orgId, record.id, {
        event_type: 'stage_changed',
        actor_id: profile.id,
        actor_name: profile.name,
        from_value: previousStage,
        to_value: record.stage,
        summary: `${profile.name} movio la oportunidad de ${previousStage} a ${record.stage}`,
      });
    }
    if (Number(record.probability) !== Number(previousProbability)) {
      recordOpportunityHistory(db, orgId, record.id, {
        event_type: 'probability_changed',
        actor_id: profile.id,
        actor_name: profile.name,
        from_value: previousProbability,
        to_value: record.probability,
        summary: `${profile.name} actualizo la probabilidad de ${previousProbability}% a ${record.probability}%`,
      });
    }
    if (record.stage === 'Propuesta enviada') {
      createAutomaticFollowUp(db, orgId, record, record.owner_id, record.owner_name);
    }
    writeDb(db);
    return { data: hydrateOpportunity(record, db), status: 200 };
  }

  if (normalizedUrl.startsWith('/opportunities/') && method === 'DELETE') {
    const recordId = normalizedUrl.split('/').pop();
    db.opportunities = db.opportunities.filter((item: any) => !(item.id === recordId && item.tenant_id === orgId));
    db.activities = db.activities.filter((item: any) => item.opportunity_id !== recordId);
    db.opportunityHistory = db.opportunityHistory.filter((item: any) => item.opportunity_id !== recordId);
    writeDb(db);
    return { data: { success: true }, status: 200 };
  }

  if (normalizedUrl === '/opportunities/export/csv' && method === 'GET') {
    return { data: toBlob(rowsToCsv(getOrgRecords(db, 'opportunities', orgId).map((item: any) => hydrateOpportunity(item, db))), 'text/csv;charset=utf-8'), status: 200 };
  }

  if (normalizedUrl === '/activities' && method === 'GET') {
    let records = getOrgRecords(db, 'activities', orgId);
    if (params.search) {
      records = records.filter((item: any) => matchesSearch(item, params.search, ['subject', 'description', 'related_to']));
    }
    if (params.status) {
      records = records.filter((item: any) => item.status === params.status);
    }
    return { data: records, status: 200 };
  }

  if (normalizedUrl === '/activities' && method === 'POST') {
    return { data: createActivity(db, orgId, profile, payload || {}), status: 201 };
  }

  if (normalizedUrl.startsWith('/activities/') && method === 'PUT') {
    const recordId = normalizedUrl.split('/').pop();
    return { data: updateActivity(db, orgId, payload || {}, recordId || ''), status: 200 };
  }

  if (normalizedUrl.startsWith('/activities/') && method === 'DELETE') {
    const recordId = normalizedUrl.split('/').pop();
    db.activities = db.activities.filter((item: any) => !(item.id === recordId && item.tenant_id === orgId));
    writeDb(db);
    return { data: { success: true }, status: 200 };
  }

  if (normalizedUrl === '/attachments' && method === 'GET') {
    const records = getOrgRecords(db, 'attachments', orgId).filter((item: any) => item.entity_type === params.entity_type && item.entity_id === params.entity_id);
    return { data: records, status: 200 };
  }

  if (normalizedUrl === '/attachments/upload' && method === 'POST') {
    const file = payload?.get?.('file');
    if (!file) errorResponse('Archivo requerido', 400);
    const encoded = arrayToBase64(await file.arrayBuffer());
    const record = {
      id: uid('attachment'),
      tenant_id: orgId,
      entity_type: payload.get('entity_type'),
      entity_id: payload.get('entity_id'),
      original_name: file.name,
      content_type: file.type || 'application/octet-stream',
      size: file.size,
      content_base64: encoded,
      uploaded_by: profile.id,
      uploaded_by_name: profile.name,
      created_at: nowIso(),
    };
    db.attachments.unshift(record);
    writeDb(db);
    return { data: record, status: 201 };
  }

  if (normalizedUrl.startsWith('/attachments/') && normalizedUrl.endsWith('/download') && method === 'GET') {
    const parts = normalizedUrl.split('/');
    const recordId = parts[2];
    const record = db.attachments.find((item: any) => item.id === recordId && item.tenant_id === orgId);
    if (!record) errorResponse('Archivo no encontrado', 404);
    return { data: base64ToBlob(record.content_base64, record.content_type), status: 200 };
  }

  if (normalizedUrl.startsWith('/attachments/') && method === 'DELETE') {
    const recordId = normalizedUrl.split('/').pop();
    db.attachments = db.attachments.filter((item: any) => !(item.id === recordId && item.tenant_id === orgId));
    writeDb(db);
    return { data: { success: true }, status: 200 };
  }

  if (normalizedUrl === '/search' && method === 'GET') {
    const query = String(params.q || '').trim();
    if (!query) return { data: { companies: [], contacts: [], leads: [], opportunities: [], activities: [] }, status: 200 };
    const searchMatches = (collection: string, fields: string[]) =>
      getOrgRecords(db, collection as any, orgId)
        .filter((item: any) => matchesSearch(item, query, fields))
        .slice(0, 8);
    return {
      data: {
        companies: searchMatches('companies', ['name', 'razon_social', 'rut', 'giro', 'industry']),
        contacts: searchMatches('contacts', ['name', 'email', 'position', 'company_name']),
        leads: searchMatches('leads', ['name', 'email', 'company', 'source', 'status', 'discard_reason']),
        opportunities: getOrgRecords(db, 'opportunities', orgId)
          .map((item: any) => hydrateOpportunity(item, db))
          .filter((item: any) => matchesSearch(item, query, ['name', 'stage', 'account_name', 'contact_name', 'owner_name']))
          .slice(0, 8),
        activities: searchMatches('activities', ['subject', 'description', 'related_to', 'type']),
      },
      status: 200,
    };
  }

  errorResponse(`Ruta local no implementada: ${method} ${normalizedUrl}`, 404);
}
